import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Popover,
  Tab,
  Tabs,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import TaskIcon from '@mui/icons-material/Task';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

dayjs.extend(relativeTime);
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';
import { TextEditor } from '@src/components/core/text-editor/text-editor';

class InboxErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error('InboxErrorBoundary caught:', error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, textAlign: 'center' }}>
          <h3>Something went wrong</h3>
          <pre style={{ textAlign: 'left', whiteSpace: 'pre-wrap', color: 'red', fontSize: 12, maxHeight: 300, overflow: 'auto' }}>
            {this.state.error?.toString()}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: '8px 16px' }}>
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import { Autocomplete } from '@mui/material';
import { fetchClients } from '@src/api/actions/clients';
import { createTask } from '@src/api/tasks';
import clientInboxApi from '@src/api/clientInbox';
import { useMessage } from '@src/context/common';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

function ParentLevelAssignBanner({ thread, clients, onAssigned, onError }) {
  const parentCompanyId = thread.parent_company;
  const suggestion = thread.suggested_child_client;
  const childOptions = useMemo(() => {
    if (!parentCompanyId || !Array.isArray(clients)) return [];
    return clients.filter(
      (c) => String(c.parent_company_id ?? '') === String(parentCompanyId ?? ''),
    );
  }, [clients, parentCompanyId]);

  const [selectedClientId, setSelectedClientId] = useState(
    suggestion?.client_id ? String(suggestion.client_id) : '',
  );
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    (clientIdOverride) => {
      const targetId = clientIdOverride ?? selectedClientId;
      if (!targetId) return;
      setSubmitting(true);
      clientInboxApi
        .reassignThreadToClient(thread.id, { client_id: Number(targetId), rerun_analysis: true })
        .then((res) => {
          onAssigned?.(res.data);
        })
        .catch((err) => {
          onError?.(err);
        })
        .finally(() => setSubmitting(false));
    },
    [thread.id, selectedClientId, onAssigned, onError],
  );

  return (
    <Alert
      severity="warning"
      variant="outlined"
      sx={{ mt: 1 }}
      icon={<LightbulbOutlinedIcon />}
    >
      <Stack spacing={1}>
        <Typography variant="subtitle2">
          This email came through a parent-company contact for{' '}
          <strong>{thread.parent_company_name || 'Unknown parent'}</strong>. Pick the
          child client it's actually about before replying.
        </Typography>
        {suggestion?.client_id ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2">
              AI suggests: <strong>{suggestion.client_name || 'Unknown'}</strong>
              {typeof suggestion.confidence === 'number'
                ? ` (${Math.round(suggestion.confidence * 100)}% confidence)`
                : ''}
              {suggestion.reason ? ` — ${suggestion.reason}` : ''}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={submitting}
              onClick={() => submit(String(suggestion.client_id))}
            >
              Accept
            </Button>
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Assign to client</InputLabel>
            <Select
              label="Assign to client"
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              <MenuItem value="">
                <em>Select a client...</em>
              </MenuItem>
              {childOptions.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedClientId || submitting}
            onClick={() => submit()}
          >
            Assign
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending', color: 'default' },
  { value: 'analyzing', label: 'Analyzing', color: 'info' },
  { value: 'analyzed', label: 'Analyzed', color: 'success' },
  { value: 'task_created', label: 'Task created', color: 'primary' },
  { value: 'ignored', label: 'Ignored', color: 'warning' },
  { value: 'failed', label: 'Failed', color: 'error' },
];

const TASK_FILTER_OPTIONS = [
  { value: 'all', label: 'All emails' },
  { value: 'without', label: 'Needs task' },
  { value: 'with', label: 'Linked to task' },
];

const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce((map, option) => {
  map[option.value] = option.label;
  return map;
}, {});

const STATUS_COLOR_MAP = STATUS_OPTIONS.reduce((map, option) => {
  if (option.color) {
    map[option.value] = option.color;
  }
  return map;
}, {});

const CONVERSATION_STATUS_OPTIONS = [
  { value: 'all', label: 'All', color: 'default' },
  { value: 'needs_client_assignment', label: 'Needs Client Assignment', color: 'warning' },
  { value: 'needs_response', label: 'Needs Response', color: 'error' },
  { value: 'in_progress', label: 'In Progress', color: 'info' },
  { value: 'awaiting_client', label: 'Awaiting Client', color: 'warning' },
  { value: 'closed', label: 'Closed', color: 'default' },
  { value: 'snoozed', label: 'Snoozed', color: 'secondary' },
  { value: 'reporting', label: 'Reporting', color: 'default' },
];

const CONVERSATION_STATUS_LABEL_MAP = CONVERSATION_STATUS_OPTIONS.reduce((map, opt) => {
  map[opt.value] = opt.label;
  return map;
}, {});

const CONVERSATION_STATUS_COLOR_MAP = CONVERSATION_STATUS_OPTIONS.reduce((map, opt) => {
  if (opt.color) map[opt.value] = opt.color;
  return map;
}, {});

// Ordered status filter pills shown on both mobile and desktop. `value` matches a
// conversation_status (or 'all'); `countKey` maps to a dashboard-summary count.
const STATUS_FILTER_PILLS = [
  { value: 'all', label: 'All', countKey: 'total_open', color: 'primary' },
  { value: 'needs_response', label: 'Needs Response', countKey: 'needs_response', color: 'error' },
  { value: 'needs_client_assignment', label: 'Needs Client', countKey: 'needs_client_assignment', color: 'warning' },
  { value: 'in_progress', label: 'In Progress', countKey: 'in_progress', color: 'info' },
  { value: 'awaiting_client', label: 'Awaiting', countKey: 'awaiting_client', color: 'warning' },
  { value: 'snoozed', label: 'Snoozed', countKey: 'snoozed', color: 'secondary' },
  { value: 'reporting', label: 'Reporting', countKey: 'reporting', color: 'default' },
  // No count on Closed: this filters ALL closed threads, but the only closed
  // count we have is "closed today" (shown separately in the metrics strip).
  { value: 'closed', label: 'Closed', countKey: null, color: 'default' },
];

const WORK_DECISION_META = {
  no_reply: {
    label: 'No reply needed',
    color: 'default',
    severity: 'info',
    message: 'This looks like a simple acknowledgement. The current recommendation is to not send a reply.',
  },
  reply_only: {
    label: 'Reply only',
    color: 'success',
    severity: 'success',
    message: 'A reply is appropriate and no internal task is currently recommended.',
  },
  reply_and_task: {
    label: 'Reply + task',
    color: 'warning',
    severity: 'warning',
    message: 'Reply to the client and create a follow-up task for the team.',
  },
  task_only: {
    label: 'Task only',
    color: 'secondary',
    severity: 'warning',
    message: 'This looks like an operational request. Create a task instead of sending a direct reply.',
  },
  needs_human_review: {
    label: 'Needs human review',
    color: 'error',
    severity: 'error',
    message: 'This thread should be reviewed by a human before any reply is sent.',
  },
};

function renderSuggestedAction(action, index) {
  if (!action) {
    return null;
  }

  if (typeof action === 'string') {
    return (
      <Box key={`action-${index}`} px={2} py={1} borderRadius={1} bgcolor="grey.100">
        <Typography variant="body2">{action}</Typography>
      </Box>
    );
  }

  if (typeof action === 'object') {
    const { title, summary, description, owner, due_date: dueDate } = action;
    return (
      <Box
        key={`action-${index}`}
        px={2}
        py={1.5}
        borderRadius={1}
        bgcolor="grey.100"
        display="flex"
        flexDirection="column"
        gap={0.5}
      >
        {title ? (
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
        ) : null}
        {summary ? <Typography variant="body2">{summary}</Typography> : null}
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}
        {owner || dueDate ? (
          <Typography variant="caption" color="text.secondary">
            {owner ? `Owner: ${owner}` : ''}
            {owner && dueDate ? ' • ' : ''}
            {dueDate ? `Due: ${dayjs(dueDate).format('MMM D, YYYY')}` : ''}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return null;
}

function getInitialFilters() {
  return {
    search: '',
    status: 'all',
    conversation_status: 'all',
    client: null,
    task: 'all',
    owner: 'all',
  };
}

function formatWorkDecisionLabel(value) {
  const key = String(value || '').trim().toLowerCase();
  return WORK_DECISION_META[key]?.label || '';
}

function formatEmailAddress(address) {
  if (!address) {
    return '';
  }
  if (typeof address === 'string') {
    return address;
  }
  if (typeof address === 'object') {
    if (address.name && address.email) {
      return `${address.name} <${address.email}>`;
    }
    if (address.email) {
      return address.email;
    }
  }
  return String(address);
}

function normalizeAddresses(addresses) {
  if (!Array.isArray(addresses)) {
    return [];
  }
  return addresses.map((address) => formatEmailAddress(address)).filter(Boolean);
}

function parseAddressInput(value) {
  if (!value) {
    return [];
  }
  return value
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getLatestExternalMessage(message) {
  const threadMessages = Array.isArray(message?.messages) ? message.messages : [];
  const externalMessages = threadMessages.filter((item) => !item?.is_internal);
  if (!externalMessages.length) {
    return null;
  }
  return externalMessages[externalMessages.length - 1];
}

const INTERNAL_EMAILS = ['jack@leadgurus.com', 'will@leadgurus.com'];
const INTERNAL_CC_PAIRS = {
  'jack@leadgurus.com': 'will@leadgurus.com',
  'will@leadgurus.com': 'jack@leadgurus.com',
};

function isInternalEmail(email) {
  return INTERNAL_EMAILS.includes(String(email || '').trim().toLowerCase());
}

function buildReplyRecipients(message, fromEmail) {
  const latestExternal = getLatestExternalMessage(message);
  const source = latestExternal || message;

  // Reply-all: start with the sender as the primary To
  const primaryTo = normalizeAddresses(source?.from_email ? [source.from_email] : []);

  // Collect all other To and CC addresses from the original message
  const originalTo = normalizeAddresses(source?.to_addresses || []);
  const originalCc = normalizeAddresses(source?.cc_addresses || []);

  // Merge all addresses, deduplicate, and remove internal emails
  const allRecipients = [...primaryTo, ...originalTo, ...originalCc];
  const seen = new Set();
  const deduped = allRecipients.filter((addr) => {
    const lower = addr.toLowerCase();
    if (seen.has(lower) || isInternalEmail(lower)) return false;
    seen.add(lower);
    return true;
  });

  // First address is the To, rest go to CC
  const toAddrs = deduped.length ? [deduped[0]] : [];
  const ccAddrs = deduped.slice(1);

  // Auto-CC the other internal partner
  const fromLower = String(fromEmail || '').trim().toLowerCase();
  const partner = INTERNAL_CC_PAIRS[fromLower];
  if (partner && !ccAddrs.some((a) => a.toLowerCase() === partner)) {
    ccAddrs.push(partner);
  }

  return {
    to: toAddrs.join(', '),
    cc: ccAddrs.join(', '),
  };
}

const BUDGET_NOTE_BLOCK_REGEX =
  /<!-- budget-note:start -->[\s\S]*?<!-- budget-note:end -->|<p[^>]*data-budget-note=\"true\"[^>]*>[\s\S]*?<\/p>|<p[^>]*class=\"budget-note-line\"[^>]*>[\s\S]*?<\/p>/gi;
const ELLIPSIS_ARTIFACT_REGEX = /<(p|div|span)[^>]*>\s*(?:\.{3}|…)\s*<\/\1>/gi;

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function upsertBudgetNote(html, note) {
  const base = String(html || '')
    .replace(BUDGET_NOTE_BLOCK_REGEX, '')
    .replace(ELLIPSIS_ARTIFACT_REGEX, '');
  const trimmed = String(note || '').trim();
  if (!trimmed) {
    return base;
  }

  const noteHtml = escapeHtml(trimmed).replace(/\n/g, '<br>');
  const block = [
    '<p class="budget-note-line" style="margin:14px 0 0;font-size:15px;line-height:1.6;color:#374151;">',
    noteHtml,
    '</p>',
  ].join('');

  const anchor = 'If you want to keep the same budget for';
  const anchorIndex = base.indexOf(anchor);
  if (anchorIndex === -1) {
    return `${base}${block}`;
  }

  const insertAt = base.lastIndexOf('<p', anchorIndex);
  if (insertAt === -1) {
    return `${base}${block}`;
  }
  return `${base.slice(0, insertAt)}${block}${base.slice(insertAt)}`;
}


function ThreadMessage({ msg, defaultExpanded, isInternal }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyIsHtml = msg.body_text && (msg.body_text.includes('<p>') || msg.body_text.includes('<br') || msg.body_text.includes('<div'));

  return (
    <Box
      sx={{
        borderLeft: '3px solid',
        borderLeftColor: isInternal ? 'info.main' : 'grey.300',
        mb: 1,
        ml: 0,
      }}
    >
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          px: 2,
          py: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={0}>
          <Avatar sx={{ width: 28, height: 28, fontSize: 13 }}>
            {(msg.from_email || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Stack minWidth={0} flex={1}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {msg.from_email}
              {isInternal ? (
                <Chip label="Us" size="small" color="info" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }} />
              ) : null}
            </Typography>
            {!expanded ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {(msg.body_text || '').replace(/<[^>]*>/g, '').slice(0, 120)}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
        <Typography variant="caption" color="text.secondary" whiteSpace="nowrap" ml={1}>
          {msg.received_at ? dayjs(msg.received_at).fromNow() : ''}
        </Typography>
      </Box>
      <Collapse in={expanded}>
        <Box px={2} pb={2}>
          <Typography variant="caption" color="text.secondary">
            To: {(msg.to_addresses || []).join(', ') || '—'}
            {msg.cc_addresses?.length ? ` · CC: ${msg.cc_addresses.join(', ')}` : ''}
          </Typography>
          {msg.attachments?.length > 0 ? (
            <Box
              mt={1.5}
              p={1.25}
              sx={{
                bgcolor: 'warning.lighter',
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1.5,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} mb={0.75}>
                <AttachFileIcon fontSize="small" sx={{ color: 'warning.dark' }} />
                <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {msg.attachments.length} {msg.attachments.length === 1 ? 'Attachment' : 'Attachments'}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {msg.attachments.map((att) => {
                  const sizeLabel = att.size
                    ? att.size > 1024 * 1024
                      ? `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                      : `${Math.max(1, Math.round(att.size / 1024))} KB`
                    : null;
                  return (
                    <Chip
                      key={att.id}
                      size="medium"
                      icon={<AttachFileIcon />}
                      deleteIcon={<DownloadIcon />}
                      onDelete={att.file_url ? () => window.open(att.file_url, '_blank') : undefined}
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="baseline">
                          <Typography variant="body2" fontWeight={600} component="span">
                            {att.filename || 'Attachment'}
                          </Typography>
                          {sizeLabel ? (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {sizeLabel}
                            </Typography>
                          ) : null}
                        </Stack>
                      }
                      onClick={att.file_url ? () => window.open(att.file_url, '_blank') : undefined}
                      sx={{
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'warning.main',
                        cursor: att.file_url ? 'pointer' : 'default',
                        '&:hover': att.file_url ? { bgcolor: 'warning.lighter' } : undefined,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          ) : null}
          {bodyIsHtml ? (
            <Box
              mt={1}
              sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                maxHeight: 400,
                overflow: 'auto',
                '& img': { maxWidth: '100%' },
                fontSize: 14,
              }}
              dangerouslySetInnerHTML={{ __html: msg.body_text }}
            />
          ) : (
            <Box
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                bgcolor: 'grey.50',
                borderRadius: 1,
                p: 2,
                mt: 1,
                maxHeight: 400,
                overflow: 'auto',
                fontSize: 14,
              }}
            >
              {msg.body_text || 'No body text available.'}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function ClientInboxPage() {
  const message = useMessage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobilePanel, setMobilePanel] = useState('queue'); // 'queue' | 'detail'
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [showAiBrain, setShowAiBrain] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('clientInbox_showAiBrain') === '1';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('clientInbox_showAiBrain', showAiBrain ? '1' : '0');
  }, [showAiBrain]);
  const [searchParams, setSearchParams] = useSearchParams();
  const threadParam = searchParams.get('thread') || '';
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState(getInitialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [clients, setClients] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedMessageDetail, setSelectedMessageDetail] = useState(null);
  const [selectedId, setSelectedId] = useState(() => {
    const raw = searchParams.get('thread');
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  });
  const [draftValue, setDraftValue] = useState('');
  const [replyFromValue, setReplyFromValue] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replyToValue, setReplyToValue] = useState('');
  const [replyCcValue, setReplyCcValue] = useState('');
  const [senderOptions, setSenderOptions] = useState([]);
  const [defaultSender, setDefaultSender] = useState('');
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [error, setError] = useState('');
  const [budgetClientIds, setBudgetClientIds] = useState([]);
  const [budgetNote, setBudgetNote] = useState('');
  const [inspectLoading, setInspectLoading] = useState(false);
  const [regenerateLoading, setRegenerateLoading] = useState(false);
  const [draftContextNote, setDraftContextNote] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [voiceTarget, setVoiceTarget] = useState(null); // 'draft' or 'context'
  const [interimVoiceText, setInterimVoiceText] = useState('');
  const [polishLoading, setPolishLoading] = useState(false);
  const [captureText, setCaptureText] = useState('');
  const [captureScope, setCaptureScope] = useState('auto'); // 'auto', 'client', 'company'
  const [captureLoading, setCaptureLoading] = useState(false);
  const [captureResult, setCaptureResult] = useState(null);
  const recognitionRef = useRef(null);
  const draftEditorRef = useRef(null);
  const [inspectError, setInspectError] = useState('');
  const [inspectData, setInspectData] = useState(null);
  const [inspectTab, setInspectTab] = useState('sources');
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [selectedThreadIds, setSelectedThreadIds] = useState([]);
  const [bulkClosing, setBulkClosing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSending, setComposeSending] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [proposalsExpanded, setProposalsExpanded] = useState(false);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardSending, setForwardSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [undoSendData, setUndoSendData] = useState(null);
  const [scheduleAnchor, setScheduleAnchor] = useState(null);
  const [scheduledReplies, setScheduledReplies] = useState([]);
  const [aiTrendsData, setAiTrendsData] = useState(null);
  const [aiTrendsExpanded, setAiTrendsExpanded] = useState(false);
  const undoTimerRef = useCallback(() => {}, []);
  const {
    options: clientOptions,
    loading: clientsLoading,
    handleInputChange: handleClientSearch,
  } = useClientAutocomplete();
  const selectedClientOption = useMemo(
    () => clientOptions.find((opt) => String(opt.value) === String(filters.client)) || null,
    [clientOptions, filters.client],
  );


  const selectedMessage = useMemo(
    () => selectedMessageDetail || messages.find((item) => item.id === selectedId) || null,
    [messages, selectedId, selectedMessageDetail],
  );

  useEffect(() => {
    const parsed = Number(threadParam);
    const nextId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    if (nextId !== selectedId) {
      setSelectedId(nextId);
    }
  }, [threadParam]);

  useEffect(() => {
    if (threadParam) {
      return;
    }
    if (selectedId && !messages.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [messages, selectedId, threadParam]);

  useEffect(() => {
    const current = threadParam;
    const next = selectedId ? String(selectedId) : '';
    if (next && current !== next) {
      const params = new URLSearchParams(searchParams.toString());
      params.set('thread', next);
      setSearchParams(params, { replace: true });
      return;
    }
    if (!next && current) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('thread');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, selectedId, setSearchParams, threadParam]);
  const selectedAnalysisPayload = selectedMessage?.analysis_payload || {};
  const selectedQuestionRouter = selectedAnalysisPayload.question_router || null;
  const selectedOwnerAssignment = selectedAnalysisPayload.owner_assignment || null;
  const workDecision = (
    selectedAnalysisPayload.work_decision || selectedQuestionRouter?.work_decision || ''
  )
    .toString()
    .trim()
    .toLowerCase();
  const workDecisionMeta = WORK_DECISION_META[workDecision] || null;
  const sendBlocked = false;
  const createTaskRecommended =
    workDecision === 'reply_and_task' ||
    workDecision === 'task_only' ||
    workDecision === 'needs_human_review';
  const isLockedBudgetConfirmationDraft = useMemo(() => {
    const processingNotes = String(selectedMessage?.processing_notes || '').toLowerCase();
    return processingNotes.includes('budget confirmation draft');
  }, [selectedMessage]);

  const draftChanged = useMemo(() => {
    if (!selectedMessage) {
      return false;
    }
    if (isLockedBudgetConfirmationDraft) {
      return upsertBudgetNote(draftValue, budgetNote) !== (selectedMessage.reply_draft || '');
    }
    return draftValue !== (selectedMessage.reply_draft || '');
  }, [budgetNote, draftValue, isLockedBudgetConfirmationDraft, selectedMessage]);

  const taskFilterValue = filters.task;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }
        return { ...prev, search: searchInput };
      });
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [searchInput]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }, []);

  // Count of active secondary filters (everything except status pills + search),
  // used for the mobile filter-button badge.
  const secondaryFilterCount = useMemo(
    () =>
      (filters.client ? 1 : 0) +
      (filters.owner !== 'all' ? 1 : 0) +
      (filters.task !== 'all' ? 1 : 0) +
      (filters.status !== 'all' ? 1 : 0),
    [filters.client, filters.owner, filters.task, filters.status],
  );

  const loadClients = useCallback(() => {
    fetchClients()
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const normalized = (Array.isArray(data) ? data : []).map((client) => ({
          id: String(client.id),
          name: client.name || 'Unnamed client',
          parent_company_id: client.parent_company ?? client.parent_company_id ?? null,
        }));
        normalized.sort((a, b) => a.name.localeCompare(b.name));
        setClients(normalized);
      })
      .catch((err) => {
        console.error('Failed to load clients for inbox filters', err);
        message.error('Unable to load clients for the inbox filter.');
      });
  }, [message]);

  const loadMessages = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {
      page,
      ordering: '-received_at',
    };
    if (pageSize) {
      params.page_size = pageSize;
    }

    if (filters.search) {
      params.search = filters.search;
    }
    if (filters.status && filters.status !== 'all') {
      params.processing_status = filters.status;
    }
    if (filters.conversation_status && filters.conversation_status !== 'all') {
      params.conversation_status = filters.conversation_status;
    }
    if (filters.client) {
      params.client = filters.client;
    }
    if (taskFilterValue === 'with') {
      params.has_task = 'true';
    } else if (taskFilterValue === 'without') {
      params.has_task = 'false';
    }
    if (filters.owner === 'me') {
      params.owner = 'me';
    }

    clientInboxApi
      .listEmailMessages(params)
      .then((response) => {
        const payload = response.data;
        const results = Array.isArray(payload)
          ? payload
          : payload?.results || [];
        const count = Array.isArray(payload)
          ? payload.length
          : payload?.count ?? results.length;

        setMessages(results);
        setTotalCount(count);
        if (Array.isArray(payload)) {
          const derived = results.length || pageSize || 25;
          if (derived !== pageSize) {
            setPageSize(derived);
          }
        } else if (payload?.page_size && payload.page_size !== pageSize) {
          setPageSize(payload.page_size);
        }
        setSelectedId((current) => {
          if (results.length === 0) {
            setSelectedMessageDetail(null);
            return null;
          }
          const urlSelectedId = (() => {
            const parsed = Number(threadParam);
            return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
          })();
          if (urlSelectedId) {
            return urlSelectedId;
          }
          return results.some((item) => item.id === current)
            ? current
            : results[0].id;
        });
      })
      .catch((err) => {
        console.error('Failed to load client inbox messages', err);
        setError('Unable to load client inbox messages.');
        message.error('Unable to load client inbox messages.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filters.client, filters.conversation_status, filters.owner, filters.search, filters.status, message, page, pageSize, taskFilterValue, threadParam]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    clientInboxApi
      .listEmailSenders()
      .then((response) => {
        const senders = response?.data?.senders || [];
        const resolvedDefault = response?.data?.default || '';
        setSenderOptions(senders);
        setDefaultSender(resolvedDefault);
        setReplyFromValue((current) => {
          if (current && senders.includes(current)) {
            return current;
          }
          if (resolvedDefault && senders.includes(resolvedDefault)) {
            return resolvedDefault;
          }
          return senders[0] || '';
        });
      })
      .catch((err) => {
        console.error('Failed to load available senders', err);
        setSenderOptions([]);
        message.error('Unable to load available sender addresses.');
      });
  }, [message]);

  const loadDashboardSummary = useCallback(() => {
    const params = {};
    if (filters.client) params.client = filters.client;
    clientInboxApi.getDashboardSummary(params)
      .then((response) => setDashboardSummary(response.data || null))
      .catch(() => {});
  }, [filters.client]);

  const loadProposals = useCallback(() => {
    clientInboxApi.listKnowledgeProposals({ status: 'pending' })
      .then((response) => {
        const results = response.data?.results || response.data || [];
        setProposals(Array.isArray(results) ? results : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadMessages();
    loadDashboardSummary();
    loadProposals();
  }, [loadMessages, loadDashboardSummary, loadProposals]);

  // Auto-save draft every 3 seconds after user stops typing
  const autoSaveRef = useCallback(
    (() => {
      let timer = null;
      let lastSaved = '';
      return (threadId, value) => {
        if (timer) clearTimeout(timer);
        if (!threadId || !value || value === lastSaved) return;
        timer = setTimeout(() => {
          clientInboxApi.updateEmailMessage(threadId, { reply_draft: value })
            .then(() => { lastSaved = value; })
            .catch(() => {});
        }, 3000);
      };
    })(),
    [],
  );

  useEffect(() => {
    if (selectedId && draftValue) {
      autoSaveRef(selectedId, draftValue);
    }
  }, [selectedId, draftValue, autoSaveRef]);

  useEffect(() => {
    if (!selectedId) {
      setSelectedMessageDetail(null);
      return undefined;
    }
    let active = true;
    setSelectedMessageDetail(null);
    setDetailLoading(true);
    clientInboxApi.markThreadRead(selectedId).catch(() => {});
    clientInboxApi
      .retrieveEmailMessage(selectedId)
      .then((response) => {
        if (!active) {
          return;
        }
        setSelectedMessageDetail(response.data || null);
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        console.error('Failed to load client inbox message detail', err);
        message.error('Unable to load the full email detail.');
        setSelectedMessageDetail(null);
      })
      .finally(() => {
        if (active) {
          setDetailLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [message, selectedId]);

  useEffect(() => {
    if (selectedMessage) {
      const nextDraft = selectedMessage.reply_draft || '';
      setDraftValue(nextDraft);
      setBudgetNote('');
      const baseSubject = selectedMessage.subject || 'Client email';
      setReplySubject(baseSubject);
      const sender = isLockedBudgetConfirmationDraft && defaultSender ? defaultSender : replyFromValue;
      const recipients = buildReplyRecipients(selectedMessage, sender);
      setReplyToValue(recipients.to);
      setReplyCcValue(recipients.cc);
      if (isLockedBudgetConfirmationDraft && defaultSender) {
        setReplyFromValue(defaultSender);
      }
    } else {
      setDraftValue('');
      setBudgetNote('');
      setReplySubject('');
      setReplyToValue('');
      setReplyCcValue('');
    }
    setInspectData(null);
    setInspectError('');
    setInspectTab('sources');
  }, [defaultSender, isLockedBudgetConfirmationDraft, replyFromValue, selectedMessage]);

  // Re-compute CC when sender changes (auto-CC partner)
  useEffect(() => {
    if (selectedMessage && replyFromValue) {
      const recipients = buildReplyRecipients(selectedMessage, replyFromValue);
      setReplyCcValue(recipients.cc);
    }
  }, [replyFromValue, selectedMessage]);

  const handleToggleThread = useCallback((threadId) => {
    setSelectedThreadIds((prev) =>
      prev.includes(threadId) ? prev.filter((id) => id !== threadId) : [...prev, threadId],
    );
  }, []);

  const handleSelectAllThreads = useCallback(() => {
    setSelectedThreadIds((prev) =>
      prev.length === messages.length ? [] : messages.map((m) => m.id),
    );
  }, [messages]);

  const handleBulkClose = useCallback(() => {
    if (selectedThreadIds.length === 0) return;
    setBulkClosing(true);
    clientInboxApi
      .bulkAction({ action: 'close', thread_ids: selectedThreadIds })
      .then(() => {
        message.success(`Closed ${selectedThreadIds.length} thread${selectedThreadIds.length > 1 ? 's' : ''}`);
        setSelectedThreadIds([]);
        loadMessages();
        loadDashboardSummary();
      })
      .catch((err) => {
        console.error('Bulk close failed', err);
        message.error('Failed to close selected threads.');
      })
      .finally(() => setBulkClosing(false));
  }, [loadDashboardSummary, loadMessages, message, selectedThreadIds]);

  // Clear selection when messages change (e.g. filter/page change)
  useEffect(() => {
    setSelectedThreadIds([]);
  }, [filters, page]);

  const handleInspectContext = useCallback(
    ({ includePrompt = false, includeBudgetPacing = false } = {}) => {
      if (!selectedMessage) {
        return;
      }
      setInspectLoading(true);
      setInspectError('');
      clientInboxApi
        .inspectEmailThreadContext(selectedMessage.id, {
          include_prompt: includePrompt,
          include_budget_pacing: includeBudgetPacing,
        })
        .then((response) => {
          setInspectData(response.data);
          if (includePrompt) {
            setInspectTab('prompt');
          } else if (includeBudgetPacing) {
            setInspectTab('live');
          } else {
            setInspectTab('sources');
          }
        })
        .catch((err) => {
          console.error('Failed to inspect thread context', err);
          setInspectError('Unable to inspect AI context for this thread.');
          message.error('Unable to inspect AI context for this thread.');
        })
        .finally(() => {
          setInspectLoading(false);
        });
    },
    [message, selectedMessage],
  );

  const handleExcludeReplyExample = useCallback(
    (messageId) => {
      if (!selectedMessage || !messageId) {
        return;
      }
      clientInboxApi
        .toggleReplyExample(selectedMessage.id, { message_id: messageId, exclude: true })
        .then(() => {
          message.success('Reply example excluded from future AI drafts.');
          handleInspectContext({ includePrompt: inspectTab === 'prompt' });
        })
        .catch((err) => {
          console.error('Failed to exclude reply example', err);
          message.error('Unable to exclude this reply example.');
        });
    },
    [handleInspectContext, inspectTab, message, selectedMessage],
  );

  const toggleVoiceInput = useCallback(
    (target = 'draft') => {
      if (isListening) {
        recognitionRef.current?.stop();
        return;
      }
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        message.warning('Speech recognition is not supported in this browser.');
        return;
      }
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = true;
      recognition.continuous = true;
      recognitionRef.current = recognition;

      const isDraft = target === 'draft';
      const setter = target === 'draft' ? setDraftValue : target === 'capture' ? setCaptureText : setDraftContextNote;

      setVoiceTarget(target);
      setInterimVoiceText('');

      const appendFinal = (text) => {
        const trimmed = text.trim();
        if (!trimmed) return;
        if (isDraft) {
          // Append through the editor so the user's cursor and selection stay
          // put while dictation continues. Falling back to the controlled-prop
          // path would call setContent() and wipe the selection.
          if (draftEditorRef.current) {
            draftEditorRef.current.appendHtml(`<p>${trimmed}</p>`);
            return;
          }
          setter((current) => (current || '') + `<p>${trimmed}</p>`);
          return;
        }
        setter((current) => {
          const currentText = current || '';
          const separator = currentText && !/\s$/.test(currentText) ? ' ' : '';
          return (currentText + separator + trimmed).trimStart();
        });
      };

      recognition.onresult = (event) => {
        let finalPart = '';
        let interimPart = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalPart += t;
          } else {
            interimPart += t;
          }
        }
        if (finalPart) appendFinal(finalPart);
        setInterimVoiceText(interimPart.trim());
      };
      recognition.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        setIsListening(false);
        setVoiceTarget(null);
        setInterimVoiceText('');
      };
      recognition.onend = () => {
        setIsListening(false);
        setVoiceTarget(null);
        setInterimVoiceText('');
      };
      recognition.start();
      setIsListening(true);
    },
    [isListening, message],
  );

  const handlePolishDraft = useCallback(() => {
    if (!selectedMessage || !draftValue.trim()) return;
    // Strip HTML tags to get raw text for the AI
    const tmp = document.createElement('div');
    tmp.innerHTML = draftValue;
    const rawText = tmp.textContent || tmp.innerText || '';
    if (!rawText.trim()) return;

    setPolishLoading(true);
    clientInboxApi
      .polishDraft(selectedMessage.id, { raw_text: rawText.trim() })
      .then((res) => {
        const polished = res?.data?.polished_html || '';
        if (polished) {
          setDraftValue(polished);
          message.success('Draft polished by AI');
        }
      })
      .catch((err) => {
        console.error('Failed to polish draft', err);
        message.error('Unable to polish draft with AI.');
      })
      .finally(() => {
        setPolishLoading(false);
      });
  }, [selectedMessage, draftValue, message]);

  const handleQuickCapture = useCallback(() => {
    if (!selectedMessage || !captureText.trim()) return;
    setCaptureLoading(true);
    setCaptureResult(null);
    clientInboxApi
      .quickCapture(selectedMessage.id, { raw_text: captureText.trim(), scope: captureScope })
      .then((res) => {
        const data = res?.data || {};
        setCaptureResult(data);
        const createdCount = (data.created || []).length;
        const updatedCount = (data.updated || []).length;
        const skippedCount = (data.skipped || []).length;
        if (createdCount > 0 || updatedCount > 0) {
          const parts = [];
          if (createdCount > 0) parts.push(`${createdCount} created`);
          if (updatedCount > 0) parts.push(`${updatedCount} updated`);
          message.success(`${parts.join(', ')}`);
          if (skippedCount > 0) {
            const reasons = (data.skipped || []).map((s) => s.skip_reason || s.title).join('; ');
            message.info(`${skippedCount} skipped (already exists): ${reasons}`);
          }
          setCaptureText('');
        } else if (skippedCount > 0) {
          message.info('All records already exist in the knowledge base. Nothing new to save.');
        } else {
          message.warning('No records were created. Try being more specific.');
        }
      })
      .catch((err) => {
        console.error('Quick capture failed', err);
        message.error('Failed to capture knowledge. Please try again.');
      })
      .finally(() => {
        setCaptureLoading(false);
      });
  }, [selectedMessage, captureText, captureScope, message]);

  // ----------------------------------------------------------------- Scheduled Send
  const handleScheduleReply = useCallback(
    (scheduledAt) => {
      if (!selectedMessage) return;
      const bodyToSend = isLockedBudgetConfirmationDraft
        ? upsertBudgetNote(draftValue, budgetNote)
        : draftValue;
      if (!bodyToSend.trim() || !replyFromValue) {
        message.error('Fill in the reply body and select a sender first.');
        return;
      }
      clientInboxApi
        .scheduleReply(selectedMessage.id, {
          body: bodyToSend,
          subject: replySubject,
          to: parseAddressInput(replyToValue),
          cc: parseAddressInput(replyCcValue),
          from_email: replyFromValue,
          scheduled_at: scheduledAt,
        })
        .then((res) => {
          message.success(`Reply scheduled for ${dayjs(scheduledAt).format('ddd MMM D, h:mm A')}`);
          setDraftValue('');
          setBudgetNote('');
          setScheduleAnchor(null);
          // Refresh scheduled replies list
          loadScheduledReplies(selectedMessage.id);
        })
        .catch((err) => {
          console.error('Failed to schedule reply', err);
          message.error('Unable to schedule reply.');
        });
    },
    [selectedMessage, draftValue, budgetNote, isLockedBudgetConfirmationDraft, replyFromValue, replySubject, replyToValue, replyCcValue, message],
  );

  const loadScheduledReplies = useCallback((threadId) => {
    clientInboxApi.getScheduledReplies(threadId).then((res) => {
      setScheduledReplies(res?.data?.scheduled_replies || []);
    }).catch(() => setScheduledReplies([]));
  }, []);

  const handleCancelScheduled = useCallback(
    (srId) => {
      if (!selectedMessage) return;
      clientInboxApi.cancelScheduledReply(selectedMessage.id, srId).then(() => {
        message.success('Scheduled reply cancelled.');
        loadScheduledReplies(selectedMessage.id);
      }).catch(() => message.error('Failed to cancel.'));
    },
    [selectedMessage, message, loadScheduledReplies],
  );

  // Load scheduled replies when selecting a thread
  useEffect(() => {
    if (selectedMessage?.id) {
      loadScheduledReplies(selectedMessage.id);
    } else {
      setScheduledReplies([]);
    }
  }, [selectedMessage?.id, loadScheduledReplies]);

  // ----------------------------------------------------------------- AI Accuracy Trends
  const loadAiTrends = useCallback((clientId) => {
    clientInboxApi.getAiAccuracyTrends({ client: clientId || '', weeks: 12 }).then((res) => {
      setAiTrendsData(res?.data || null);
    }).catch(() => setAiTrendsData(null));
  }, []);

  // Stop speech recognition on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  const handleRegenerateDraft = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    setRegenerateLoading(true);
    setInspectError('');
    const payload = {};
    if (draftContextNote.trim()) {
      payload.additional_context = draftContextNote.trim();
    }
    clientInboxApi
      .regenerateEmailThreadDraft(selectedMessage.id, payload)
      .then((response) => {
        const payload = response.data || {};
        const updatedThread = payload.thread;
        const inspection = payload.inspection;
        if (updatedThread) {
          setMessages((prev) =>
            prev.map((item) => (item.id === updatedThread.id ? { ...item, ...updatedThread } : item)),
          );
          setSelectedMessageDetail(updatedThread);
          setDraftValue(updatedThread.reply_draft || '');
        }
        if (inspection) {
          setInspectData(inspection);
          setInspectTab('changes');
          if (inspection?.draft_comparison?.changed) {
            message.success('Draft regenerated with updated AI context.');
          } else {
            message.success('Draft regenerated. Context stayed materially the same.');
          }
        } else {
          message.success('Draft regenerated.');
        }
      })
      .catch((err) => {
        console.error('Failed to regenerate reply draft', err);
        setInspectError('Unable to regenerate the AI draft for this thread.');
        message.error('Unable to regenerate the AI draft for this thread.');
      })
      .finally(() => {
        setRegenerateLoading(false);
      });
  }, [message, selectedMessage, draftContextNote]);

  const renderInspectorSection = useCallback(() => {
    if (!inspectData) {
      return (
        <Typography variant="body2" color="text.secondary">
          Load AI Brain Inspector to review sources used, selected knowledge, memory, live data, and the final prompt.
        </Typography>
      );
    }

    if (inspectTab === 'sources') {
      const sources = inspectData.sources_used || {};
      const sections = [
        {
          label: 'Profile context',
          items: (sources.profile && sources.profile.items) || [],
          render: (item) => `${item.title}${item.kind ? ` • ${item.kind}` : ''}`,
        },
        {
          label: 'Knowledge',
          items: sources.knowledge || [],
          render: (item) =>
            `${item.title}${item.chunk_type ? ` • ${item.chunk_type}` : ''}${
              item.topic ? ` (${item.topic})` : ''
            }`,
        },
        {
          label: 'Memory',
          items: sources.memory || [],
          render: (item) => `#${item.id} ${item.title}${item.category ? ` • ${item.category}` : ''}`,
        },
        {
          label: 'Recent client emails',
          items: sources.recent_relationship_messages || [],
          render: (item) => `#${item.id} ${item.subject}`,
        },
        {
          label: 'Recent reply examples',
          items: sources.recent_reply_examples || [],
          render: (item) => `#${item.id} ${item.subject}`,
          action: (item) => (
            <Tooltip title="Stop using this reply as an AI example (filters it out of future drafts)">
              <Button
                size="small"
                color="warning"
                variant="text"
                onClick={() => handleExcludeReplyExample(item.id)}
                sx={{ minWidth: 0, textTransform: 'none', fontSize: 12 }}
              >
                Exclude
              </Button>
            </Tooltip>
          ),
        },
      ];

      return (
        <Stack spacing={1.5}>
          {sections.map((section) => (
            <Box key={section.label} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">{section.label}</Typography>
              {section.items.length ? (
                <Stack spacing={0.75} mt={1}>
                  {section.items.map((item) => (
                    <Stack
                      key={item.key || `${section.label}-${item.id}`}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {section.render(item)}
                      </Typography>
                      {section.action ? section.action(item) : null}
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No sources selected.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'changes') {
      const sourceChanges = inspectData.source_changes || {};
      const sections = Object.values(sourceChanges.sections || {});
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Draft comparison</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {inspectData.draft_comparison?.changed
                ? 'The regenerated draft changed.'
                : 'The regenerated draft matched the previous version.'}
            </Typography>
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Context diff summary</Typography>
            {(sourceChanges.summary || []).length ? (
              <Stack spacing={0.75} mt={1}>
                {sourceChanges.summary.map((line) => (
                  <Typography key={line} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No source changes relative to the previous saved draft.
              </Typography>
            )}
          </Box>
          {sections.map((section) => (
            <Box key={section.label} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">{section.label}</Typography>
              {section.changed ? (
                <Stack spacing={0.75} mt={1}>
                  {(section.added || []).map((item) => (
                    <Typography key={`added-${section.label}-${item}`} variant="body2" color="success.main">
                      + {item}
                    </Typography>
                  ))}
                  {(section.removed || []).map((item) => (
                    <Typography key={`removed-${section.label}-${item}`} variant="body2" color="error.main">
                      - {item}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  No changes.
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'knowledge') {
      const snippets = inspectData.knowledge_snippets || [];
      if (!snippets.length) {
        return <Typography variant="body2" color="text.secondary">No knowledge snippets selected.</Typography>;
      }
      return (
        <Stack spacing={1.5}>
          {snippets.map((snippet, index) => (
            <Box key={`${snippet.title}-${index}`} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">
                {snippet.title} {snippet.chunk_type ? `• ${snippet.chunk_type}` : ''}
              </Typography>
              {snippet.topic ? (
                <Typography variant="caption" color="text.secondary">
                  Topic: {snippet.topic}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {snippet.content}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'routing') {
      const router = inspectData.question_router || {};
      const recommendedActions = router.recommended_actions || [];
      const executedActions = inspectData.backend_actions || [];
      const actionResults = inspectData.backend_action_results || [];
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Question router</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.routing || 'No routing decision available.'}
            </Typography>
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Work decision</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {WORK_DECISION_META[router.work_decision]?.label ||
                router.work_decision ||
                'No work decision available.'}
            </Typography>
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Recommended actions</Typography>
            {recommendedActions.length ? (
              <Stack spacing={0.75} mt={1}>
                {recommendedActions.map((action, index) => (
                  <Typography key={`recommended-${action.type || index}`} variant="body2">
                    {action.type}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No router-recommended backend actions.
              </Typography>
            )}
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Executed actions</Typography>
            {executedActions.length ? (
              <Stack spacing={0.75} mt={1}>
                {executedActions.map((action, index) => (
                  <Typography key={`executed-${action.type || index}`} variant="body2">
                    {action.type}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No backend actions were executed.
              </Typography>
            )}
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Action results</Typography>
            {actionResults.length ? (
              <Stack spacing={0.75} mt={1}>
                {actionResults.map((result, index) => (
                  <Typography key={`result-${result.type || index}`} variant="body2">
                    {result.type || 'action'}
                    {result.success === false ? ' • failed' : ' • completed'}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No backend action results stored yet.
              </Typography>
            )}
          </Box>
          {inspectData.question_router ? (
            <Box p={1.5} borderRadius={1} bgcolor="grey.50">
              <Box
                component="pre"
                sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12, overflow: 'auto' }}
              >
                {JSON.stringify(inspectData.question_router, null, 2)}
              </Box>
            </Box>
          ) : null}
        </Stack>
      );
    }

    if (inspectTab === 'profile') {
      return (
        <Box p={1.5} borderRadius={1} bgcolor="grey.50">
          <Typography variant="subtitle2">Client profile context</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
            {inspectData.sections?.profile || 'No client profile context available.'}
          </Typography>
        </Box>
      );
    }

    if (inspectTab === 'memory') {
      const items = inspectData.memory_items || [];
      if (!items.length) {
        return <Typography variant="body2" color="text.secondary">No memory items selected.</Typography>;
      }
      return (
        <Stack spacing={1.5}>
          {items.map((item) => (
            <Box key={item.id} p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">
                #{item.id} {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.category} • score {(item.score || 0).toFixed(3)}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {item.summary}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    if (inspectTab === 'live') {
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Live snapshot</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.live_data || 'No live data available.'}
            </Typography>
          </Box>
          {inspectData.budget_pacing ? (
            <Box p={1.5} borderRadius={1} bgcolor="grey.50">
              <Typography variant="subtitle2">Budget pacing action</Typography>
              <Box
                component="pre"
                sx={{ m: 0, mt: 1, whiteSpace: 'pre-wrap', fontSize: 12, overflow: 'auto' }}
              >
                {JSON.stringify(inspectData.budget_pacing, null, 2)}
              </Box>
            </Box>
          ) : null}
        </Stack>
      );
    }

    if (inspectTab === 'recent') {
      return (
        <Stack spacing={1.5}>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Recent relationship memory</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.recent_relationship_memory || 'No recent relationship memory.'}
            </Typography>
          </Box>
          <Box p={1.5} borderRadius={1} bgcolor="grey.50">
            <Typography variant="subtitle2">Recent reply examples</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
              {inspectData.sections?.recent_reply_examples || 'No recent reply examples.'}
            </Typography>
          </Box>
        </Stack>
      );
    }

    if (inspectTab === 'prompt') {
      return (
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'grey.50',
            whiteSpace: 'pre-wrap',
            fontSize: 12,
            maxHeight: 420,
            overflow: 'auto',
          }}
        >
          {inspectData.prompt || 'Prompt not loaded.'}
        </Box>
      );
    }

    return null;
  }, [inspectData, inspectTab]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    clientInboxApi.triggerIngest()
      .then((res) => {
        const count = res?.data?.ingested || 0;
        if (count > 0) {
          message.success(`${count} new email${count !== 1 ? 's' : ''} ingested.`);
        }
      })
      .catch(() => {})
      .finally(() => {
        loadMessages();
        loadDashboardSummary();
      });
  }, [loadDashboardSummary, loadMessages, message]);

  const handleSaveDraft = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    const draftToSave = isLockedBudgetConfirmationDraft
      ? upsertBudgetNote(draftValue, budgetNote)
      : draftValue;
    setSavingDraft(true);
    clientInboxApi
      .updateEmailMessage(selectedMessage.id, { reply_draft: draftToSave })
      .then((response) => {
        const updated = response.data;
        setMessages((prev) =>
          prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
        );
        setSelectedMessageDetail(updated);
        if (isLockedBudgetConfirmationDraft) {
          setDraftValue(draftToSave);
          setBudgetNote('');
        }
        message.success('Draft saved.');
      })
      .catch((err) => {
        console.error('Failed to save reply draft', err);
        message.error('Unable to save the reply draft.');
      })
      .finally(() => {
        setSavingDraft(false);
      });
  }, [budgetNote, draftValue, isLockedBudgetConfirmationDraft, message, selectedMessage]);

  const handleCreateTask = useCallback(() => {
    if (!selectedMessage || selectedMessage.created_task) {
      return;
    }
    setCreatingTask(true);
    const payload = {
      client: selectedMessage.client,
      title: selectedMessage.subject || 'Client email follow-up',
      notes: selectedMessage.summary || '',
      description: selectedMessage.body_text || '',
      status: 'backlog',
      priority: 'medium',
    };

    createTask(payload)
      .then((taskResponse) => {
        const task = taskResponse.data;
        return clientInboxApi
          .updateEmailMessage(selectedMessage.id, {
            created_task: task.id,
            processing_status: 'task_created',
          })
          .then((emailResponse) => {
            const updated = emailResponse.data;
            setMessages((prev) =>
              prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)),
            );
            setSelectedMessageDetail(updated);
            message.success('Task created and linked to the email.');
          });
      })
      .catch((err) => {
        console.error('Failed to create a task for the email', err);
        message.error('Unable to create a task for this email.');
      })
      .finally(() => {
        setCreatingTask(false);
      });
  }, [message, selectedMessage]);

  const handleSendReply = useCallback(() => {
    if (!selectedMessage) {
      return;
    }
    if (!replyFromValue) {
      message.error('Select a sender before sending.');
      return;
    }
    if (sendBlocked) {
      message.error('This thread is currently marked as not ready to send from the AI workflow.');
      return;
    }
    const bodyToSend = isLockedBudgetConfirmationDraft
      ? upsertBudgetNote(draftValue, budgetNote)
      : draftValue;
    const trimmedBody = bodyToSend.trim();
    if (!trimmedBody) {
      message.error('Reply body cannot be empty.');
      return;
    }
    let payload;
    if (replyAttachments.length > 0) {
      const formData = new FormData();
      formData.append('body', bodyToSend);
      formData.append('subject', replySubject);
      formData.append('from_email', replyFromValue);
      parseAddressInput(replyToValue).forEach((addr) => formData.append('to', addr));
      parseAddressInput(replyCcValue).forEach((addr) => formData.append('cc', addr));
      replyAttachments.forEach((file) => formData.append('attachments', file));
      payload = formData;
    } else {
      payload = {
        body: bodyToSend,
        subject: replySubject,
        to: parseAddressInput(replyToValue),
        cc: parseAddressInput(replyCcValue),
        from_email: replyFromValue,
      };
    }
    // Undo send: queue the send with a 5-second delay
    const threadId = selectedMessage.id;
    setUndoSendData({ threadId, payload });
    setSendingReply(true);

    const timer = setTimeout(() => {
      clientInboxApi
        .replyToEmailMessage(threadId, payload)
        .then((response) => {
          try {
            const updated = response.data;
            // Update the detail pane with full response data
            setSelectedMessageDetail(updated);
            // Update only list-relevant fields in the messages array
            setMessages((prev) =>
              prev.map((item) =>
                item.id === updated.id
                  ? {
                      ...item,
                      conversation_status: updated.conversation_status,
                      reply_draft: updated.reply_draft,
                      processing_status: updated.processing_status,
                      message_count: updated.message_count,
                      last_received_at: updated.last_received_at,
                      is_read: true,
                    }
                  : item,
              ),
            );
            // Navigate to next needs_response thread after state settles
            setTimeout(() => {
              setMessages((current) => {
                const currentIndex = current.findIndex((item) => item.id === updated.id);
                const nextThread = current.find(
                  (item, idx) => idx !== currentIndex && item.conversation_status === 'needs_response',
                );
                if (nextThread) {
                  setSelectedId(nextThread.id);
                  setSearchParams({ thread: String(nextThread.id) });
                }
                return current; // don't mutate
              });
            }, 100);
            setDraftValue('');
            setBudgetNote('');
            setReplyAttachments([]);
            message.success('Reply sent.');
            loadDashboardSummary();
          } catch (innerErr) {
            console.error('Reply success handler crashed:', innerErr);
            message.error('Reply sent but UI failed to update. Please refresh.');
          }
        })
        .catch((err) => {
          console.error('Failed to send reply', err);
          message.error('Unable to send the reply.');
        })
        .finally(() => {
          setSendingReply(false);
          setUndoSendData(null);
        });
    }, 5000);

    // Store the timer so undo can cancel it
    setUndoSendData((prev) => ({ ...prev, timer }));
  }, [
    budgetNote,
    draftValue,
    isLockedBudgetConfirmationDraft,
    loadDashboardSummary,
    message,
    replyCcValue,
    replyFromValue,
    replySubject,
    replyToValue,
    sendBlocked,
    selectedMessage,
    setSearchParams,
  ]);

  const handleCreateBudgetDrafts = useCallback(() => {
    if (!budgetClientIds.length) {
      message.error('Select at least one client to create budget drafts.');
      return;
    }
    setCreatingBudgetDrafts(true);
    clientInboxApi
      .createBudgetConfirmations({ client_ids: budgetClientIds })
      .then((response) => {
        const createdCount = response?.data?.created?.length || 0;
        const skippedCount = response?.data?.skipped?.length || 0;
        message.success(
          `Budget drafts created: ${createdCount}. Skipped: ${skippedCount}.`,
        );
        loadMessages();
      })
      .catch((err) => {
        console.error('Failed to create budget confirmation drafts', err);
        message.error('Unable to create budget confirmation drafts.');
      })
      .finally(() => {
        setCreatingBudgetDrafts(false);
      });
  }, [budgetClientIds, loadMessages, message]);

  const totalPages = useMemo(() => {
    if (!totalCount || !pageSize) {
      return 1;
    }
    return Math.max(1, Math.ceil(totalCount / pageSize));
  }, [pageSize, totalCount]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isMobile && mobilePanel === 'detail' ? (
              <IconButton
                onClick={() => {
                  setMobilePanel('queue');
                  setShowMobileSidebar(false);
                }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
            ) : null}
            <Typography variant={isMobile ? 'h5' : 'h4'}>Client Inbox</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => setComposeOpen(true)}
            >
              New Email
            </Button>
            {!isMobile ? (
              <Button
                variant="outlined"
                component={RouterLink}
                to="/tasks"
                startIcon={<TaskIcon />}
              >
                Task Board
              </Button>
            ) : null}
          </Stack>
        </Stack>



        {!(isMobile && mobilePanel === 'detail') ? (
          <Box
            sx={{
              position: { xs: 'sticky', lg: 'static' },
              top: 'var(--MainNav-height, 56px)',
              zIndex: 5,
              bgcolor: 'background.default',
              pt: { xs: 1, lg: 0 },
              mx: { xs: -2, md: -3 },
              px: { xs: 2, md: 3 },
            }}
          >
            <Stack spacing={1.25}>
              {/* Metric-only stats (separated from the status filters) */}
              {dashboardSummary ? (
                <Stack
                  direction="row"
                  spacing={2.5}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="baseline"
                  sx={{ px: 0.5 }}
                >
                  {dashboardSummary.ai_accuracy != null ? (
                    <Stack direction="row" spacing={0.5} alignItems="baseline">
                      <Typography variant="caption" color="text.secondary">AI Accuracy</Typography>
                      <Typography variant="subtitle2" color="success.main" fontWeight={700}>
                        {Math.round((dashboardSummary.ai_accuracy ?? 0) * 100)}%
                      </Typography>
                      {dashboardSummary.ai_evaluated ? (
                        <Typography variant="caption" color="text.disabled">
                          ({dashboardSummary.ai_evaluated} evaluated)
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                  <Stack direction="row" spacing={0.5} alignItems="baseline">
                    <Typography variant="caption" color="text.secondary">Closed today</Typography>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {dashboardSummary.closed_today ?? 0}
                    </Typography>
                  </Stack>
                  {dashboardSummary.auto_closed_today > 0 ? (
                    <Stack direction="row" spacing={0.5} alignItems="baseline">
                      <Typography variant="caption" color="text.secondary">Auto-closed today</Typography>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {dashboardSummary.auto_closed_today}
                      </Typography>
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}

              {/* Status filter pills — scroll horizontally on mobile, wrap on desktop */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  overflowX: 'auto',
                  flexWrap: { xs: 'nowrap', lg: 'wrap' },
                  pb: 0.5,
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                {STATUS_FILTER_PILLS.map((pill) => {
                  const selected =
                    pill.value === 'all'
                      ? filters.conversation_status === 'all'
                      : filters.conversation_status === pill.value;
                  const count = pill.countKey && dashboardSummary ? dashboardSummary[pill.countKey] : null;
                  const label = count > 0 ? `${pill.label} · ${count}` : pill.label;
                  return (
                    <Chip
                      key={pill.value}
                      label={label}
                      color={selected ? pill.color : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() =>
                        handleFilterChange(
                          'conversation_status',
                          pill.value === 'all' || filters.conversation_status === pill.value
                            ? 'all'
                            : pill.value,
                        )
                      }
                      sx={{ flexShrink: 0, fontWeight: selected ? 700 : 500 }}
                    />
                  );
                })}
              </Box>

              {/* Mobile: search + filters button (secondary filters live in the drawer) */}
              {isMobile ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search subject or summary"
                    size="small"
                    sx={{ flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Badge badgeContent={secondaryFilterCount} color="primary">
                    <IconButton
                      onClick={() => setFiltersDrawerOpen(true)}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                    >
                      <TuneIcon />
                    </IconButton>
                  </Badge>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        {!isMobile && dashboardSummary?.ai_accuracy != null && (
          <Card
            variant="outlined"
            sx={{ display: isMobile && mobilePanel === 'detail' ? 'none' : 'block', cursor: 'pointer' }}
            onClick={() => {
              if (!aiTrendsExpanded) {
                loadAiTrends(filters.client || '');
              }
              setAiTrendsExpanded((prev) => !prev);
            }}
          >
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" fontWeight={600}>
                  AI Accuracy Trends
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {aiTrendsExpanded ? 'Click to collapse' : 'Click to expand'}
                </Typography>
              </Stack>
              <Collapse in={aiTrendsExpanded}>
                {aiTrendsData ? (
                  <Stack spacing={2} mt={1.5}>
                    {aiTrendsData.weekly_trends?.length > 0 ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>Weekly Accuracy %</Typography>
                        <ResponsiveContainer width="100%" height={180}>
                          <LineChart data={aiTrendsData.weekly_trends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="week" tickFormatter={(v) => dayjs(v).format('M/D')} tick={{ fontSize: 11 }} />
                            <YAxis domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} width={40} />
                            <RTooltip
                              formatter={(v) => `${Math.round(v * 100)}%`}
                              labelFormatter={(v) => `Week of ${dayjs(v).format('MMM D')}`}
                            />
                            <Line type="monotone" dataKey="accuracy" stroke="#4caf50" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No accuracy data yet</Typography>
                    )}
                    {aiTrendsData.by_intent?.length > 0 ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>Accuracy by Intent (top 10)</Typography>
                        <ResponsiveContainer width="100%" height={180}>
                          <BarChart data={aiTrendsData.by_intent} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" domain={[0, 1]} tickFormatter={(v) => `${Math.round(v * 100)}%`} tick={{ fontSize: 11 }} />
                            <YAxis type="category" dataKey="intent" tick={{ fontSize: 10 }} width={100} />
                            <RTooltip formatter={(v, name) => name === 'accuracy' ? `${Math.round(v * 100)}%` : v} />
                            <Bar dataKey="accuracy" fill="#2196f3" name="Accuracy" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : null}
                    {aiTrendsData.response_time?.length > 0 ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary" gutterBottom>Avg Response Time (hours)</Typography>
                        <ResponsiveContainer width="100%" height={150}>
                          <LineChart data={aiTrendsData.response_time}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="week" tickFormatter={(v) => dayjs(v).format('M/D')} tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} width={40} />
                            <RTooltip
                              formatter={(v) => `${v}h`}
                              labelFormatter={(v) => `Week of ${dayjs(v).format('MMM D')}`}
                            />
                            <Line type="monotone" dataKey="avg_hours" stroke="#ff9800" strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : null}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>
        )}

        {proposals.length > 0 ? (
          <Card
            sx={{
              border: '1px solid',
              borderColor: 'warning.light',
              bgcolor: 'warning.lighter',
              display: isMobile && mobilePanel === 'detail' ? 'none' : 'block',
            }}
          >
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setProposalsExpanded((prev) => !prev)}
                  sx={{ cursor: 'pointer' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <LightbulbOutlinedIcon color="warning" fontSize="small" />
                    <Typography variant="subtitle2">
                      {proposals.length} knowledge suggestion{proposals.length !== 1 ? 's' : ''} based on your recent edits
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant={proposalsExpanded ? 'text' : 'outlined'}
                      onClick={(e) => {
                        e.stopPropagation();
                        setProposalsExpanded((prev) => !prev);
                      }}
                    >
                      {proposalsExpanded ? 'Collapse' : 'Review'}
                    </Button>
                  </Stack>
                </Stack>
                <Collapse in={proposalsExpanded}>
                  <Stack spacing={1.5}>
                    {proposals.map((proposal) => {
                      const isDocument = (proposal.proposal_type || '').includes('document');
                      const isClientScoped = !!proposal.client_name;
                      const sourceCount = proposal.finding_count || proposal.feedback_count || 0;
                      const approveDescription = (() => {
                        const target = isDocument ? 'playbook document' : 'memory item';
                        const audience = isClientScoped
                          ? `for ${proposal.client_name}`
                          : 'used across all clients';
                        return `Approve — creates a ${target} ${audience}`;
                      })();
                      return (
                        <Card key={proposal.id} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack spacing={1}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <Stack spacing={0.5}>
                                  <Typography variant="subtitle2">{proposal.title}</Typography>
                                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                                    <Chip
                                      label={isDocument ? 'Playbook' : 'Memory rule'}
                                      size="small"
                                      color={isDocument ? 'info' : 'default'}
                                      variant="outlined"
                                    />
                                    <Chip
                                      label={isClientScoped ? `For: ${proposal.client_name}` : 'Company-wide'}
                                      size="small"
                                      color={isClientScoped ? 'secondary' : 'primary'}
                                      variant="outlined"
                                    />
                                    {sourceCount > 0 ? (
                                      <Typography variant="caption" color="text.secondary">
                                        from {sourceCount} {sourceCount === 1 ? 'thread' : 'threads'}
                                      </Typography>
                                    ) : null}
                                  </Stack>
                                  {proposal.reason ? (
                                    <Typography variant="caption" color="text.secondary">
                                      {proposal.reason}
                                    </Typography>
                                  ) : null}
                                </Stack>
                                <Stack direction="row" spacing={0.5}>
                                  <Tooltip title={approveDescription}>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => {
                                        clientInboxApi.approveProposal(proposal.id)
                                          .then(() => {
                                            setProposals((prev) => prev.filter((p) => p.id !== proposal.id));
                                            message.success(`Approved: ${proposal.title}`);
                                          })
                                          .catch(() => message.error('Failed to approve proposal.'));
                                      }}
                                    >
                                      <CheckIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Dismiss">
                                    <IconButton
                                      size="small"
                                      color="default"
                                      onClick={() => {
                                        clientInboxApi.dismissProposal(proposal.id)
                                          .then(() => {
                                            setProposals((prev) => prev.filter((p) => p.id !== proposal.id));
                                          })
                                          .catch(() => message.error('Failed to dismiss proposal.'));
                                      }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </Stack>
                              <Box
                                sx={{
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  p: 1.5,
                                  fontSize: 13,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ whiteSpace: 'pre-wrap' }}
                                >
                                  {proposal.proposed_content}
                                </Typography>
                              </Box>
                              {proposal.proposed_tags?.length > 0 ? (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {proposal.proposed_tags.map((tag) => (
                                    <Chip key={tag} label={tag} size="small" variant="outlined" />
                                  ))}
                                </Stack>
                              ) : null}
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                </Collapse>
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        <Card sx={{ display: { xs: 'none', lg: 'block' } }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search subject or summary"
                size="small"
                sx={{ minWidth: 220, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={clientOptions}
                loading={clientsLoading}
                value={selectedClientOption}
                onChange={(_, option) => handleFilterChange('client', option ? String(option.value) : null)}
                onInputChange={handleClientSearch}
                getOptionLabel={(option) => option.label || ''}
                isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
                renderInput={(params) => (
                  <TextField {...params} label="Client" placeholder="Search clients..." />
                )}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Ownership</InputLabel>
                <Select
                  label="Ownership"
                  value={filters.owner}
                  onChange={(event) => handleFilterChange('owner', event.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="me">Mine</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Task</InputLabel>
                <Select
                  label="Task"
                  value={filters.task}
                  onChange={(event) => handleFilterChange('task', event.target.value)}
                >
                  {TASK_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Processing</InputLabel>
                <Select
                  label="Processing"
                  value={filters.status}
                  onChange={(event) => handleFilterChange('status', event.target.value)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filters.conversation_status !== 'all' || filters.client || filters.owner !== 'all' || filters.task !== 'all' || filters.status !== 'all' || filters.search ? (
                <Button
                  size="small"
                  onClick={() => {
                    setFilters(getInitialFilters());
                    setSearchInput('');
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </Stack>
          </CardContent>
        </Card>

        {/* Mobile secondary-filters drawer (Client / Ownership / Task / Processing) */}
        <Drawer
          anchor="bottom"
          open={filtersDrawerOpen}
          onClose={() => setFiltersDrawerOpen(false)}
          PaperProps={{
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh', p: 2 },
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Filters</Typography>
              <Button
                size="small"
                onClick={() => {
                  setFilters(getInitialFilters());
                  setSearchInput('');
                }}
              >
                Clear
              </Button>
            </Stack>
            <Autocomplete
              size="small"
              fullWidth
              options={clientOptions}
              loading={clientsLoading}
              value={selectedClientOption}
              onChange={(_, option) => handleFilterChange('client', option ? String(option.value) : null)}
              onInputChange={handleClientSearch}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search clients..." />
              )}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Ownership</InputLabel>
              <Select
                label="Ownership"
                value={filters.owner}
                onChange={(event) => handleFilterChange('owner', event.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="me">Mine</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Task</InputLabel>
              <Select
                label="Task"
                value={filters.task}
                onChange={(event) => handleFilterChange('task', event.target.value)}
              >
                {TASK_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Processing status</InputLabel>
              <Select
                label="Processing status"
                value={filters.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" fullWidth onClick={() => setFiltersDrawerOpen(false)}>
              Done
            </Button>
          </Stack>
        </Drawer>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Grid container spacing={3}>
          <Grid item xs={12} lg={3} sx={{ display: isMobile && mobilePanel !== 'queue' ? 'none' : 'block' }}>
            <Card sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box px={2.5} py={2} borderBottom="1px solid" borderColor="divider">
                  <Typography variant="overline" color="text.secondary">
                    Queue
                  </Typography>
                  <Typography variant="h6">Client Inbox</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalCount.toLocaleString()} email{totalCount === 1 ? '' : 's'} across current filters
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    Sorted by priority (urgency + budget + status + wait time)
                  </Typography>
                </Box>
                {messages.length > 0 && (
                  <Box
                    px={1.5}
                    py={0.75}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    borderBottom="1px solid"
                    borderColor="divider"
                    bgcolor={selectedThreadIds.length > 0 ? 'action.selected' : 'transparent'}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedThreadIds.length === messages.length && messages.length > 0}
                      indeterminate={selectedThreadIds.length > 0 && selectedThreadIds.length < messages.length}
                      onChange={handleSelectAllThreads}
                    />
                    {selectedThreadIds.length > 0 ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          {selectedThreadIds.length} selected
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          disabled={bulkClosing}
                          onClick={handleBulkClose}
                        >
                          {bulkClosing ? 'Closing...' : 'Close'}
                        </Button>
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Select all
                      </Typography>
                    )}
                  </Box>
                )}
                {loading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={6}>
                    <CircularProgress size={32} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Box px={3} py={6} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      No emails match the current filters.
                    </Typography>
                  </Box>
                ) : (
                  <List disablePadding sx={{ maxHeight: isMobile ? 'calc(100dvh - 250px)' : '72vh', overflow: 'auto' }}>
                    {messages.map((item) => {
                      const addresses = normalizeAddresses(item.to_addresses || []);
                      const initials = item.from_email ? item.from_email.charAt(0).toUpperCase() : '?';
                      const workMeta = WORK_DECISION_META[item.work_decision] || null;
                      const isUnread = item.is_read === false;
                      const convColor = CONVERSATION_STATUS_COLOR_MAP[item.conversation_status] || 'default';
                      const convLabel = CONVERSATION_STATUS_LABEL_MAP[item.conversation_status] || item.conversation_status;
                      const budgetTier = item.client_budget >= 100000 ? '$$$$' : item.client_budget >= 30000 ? '$$$' : item.client_budget >= 15000 ? '$$' : item.client_budget > 0 ? '$' : '';
                      const urgency = item.urgency_score || 0;
                      const urgencyLabel = urgency >= 75 ? 'Critical' : urgency >= 50 ? 'High' : urgency >= 25 ? 'Medium' : urgency > 0 ? 'Low' : '';
                      const urgencyColor = urgency >= 75 ? 'error' : urgency >= 50 ? 'warning' : urgency >= 25 ? 'info' : 'default';
                      // Urgency coloring for time-since-received
                      const hoursAgo = item.received_at ? dayjs().diff(dayjs(item.received_at), 'hour') : 0;
                      const timeColor = hoursAgo > 24 ? 'error.main' : hoursAgo > 8 ? 'warning.main' : hoursAgo > 2 ? 'text.secondary' : 'success.main';
                      const timeAgo = item.received_at ? dayjs(item.received_at).fromNow() : '';
                      return (
                        <ListItemButton
                          key={item.id}
                          onClick={() => {
                            setSelectedId(item.id);
                            if (isMobile) {
                              setMobilePanel('detail');
                              setShowMobileSidebar(false);
                            }
                          }}
                          selected={item.id === selectedId}
                          alignItems="flex-start"
                          sx={{
                            borderLeft: '4px solid',
                            borderLeftColor:
                              item.conversation_status === 'needs_client_assignment'
                                ? 'warning.main'
                                : item.conversation_status === 'needs_response'
                                ? 'error.main'
                                : item.conversation_status === 'in_progress'
                                ? 'info.main'
                                : item.conversation_status === 'awaiting_client'
                                ? 'warning.main'
                                : item.conversation_status === 'closed'
                                ? 'grey.400'
                                : 'transparent',
                            px: 2,
                            py: 1.5,
                            bgcolor: isUnread ? 'action.hover' : 'inherit',
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedThreadIds.includes(item.id)}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleThread(item.id);
                            }}
                            sx={{ mr: 0.5, mt: 0.5 }}
                          />
                          <ListItemAvatar>
                            <Avatar>{initials}</Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Stack spacing={0.75}>
                                <Typography variant="subtitle2" noWrap fontWeight={isUnread ? 700 : 500}>
                                  {item.subject || 'Untitled email'}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                  {item.conversation_status ? (
                                    <Chip
                                      size="small"
                                      label={convLabel}
                                      color={convColor}
                                    />
                                  ) : null}
                                  {item.work_decision ? (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      label={formatWorkDecisionLabel(item.work_decision) || item.work_decision}
                                    />
                                  ) : null}
                                  {urgencyLabel ? (
                                    <Tooltip title={item.urgency_reason || `Urgency: ${urgency}/100`} arrow>
                                      <Chip
                                        size="small"
                                        color={urgencyColor}
                                        variant={urgency < 25 ? 'outlined' : 'filled'}
                                        label={`${urgencyLabel} (${urgency})`}
                                      />
                                    </Tooltip>
                                  ) : null}
                                  {item.is_follow_up ? (
                                    <Chip size="small" color="warning" icon={<AccessAlarmIcon />} label="Follow-up" />
                                  ) : item.follow_up_at ? (
                                    <Tooltip title={`Follow-up: ${dayjs(item.follow_up_at).format('MMM D, h:mm A')}`} arrow>
                                      <Chip size="small" variant="outlined" color="info" icon={<AccessAlarmIcon />} label={dayjs(item.follow_up_at).format('MMM D')} />
                                    </Tooltip>
                                  ) : null}
                                  {item.note_count > 0 ? (
                                    <Chip size="small" variant="outlined" label={`${item.note_count} note${item.note_count > 1 ? 's' : ''}`} />
                                  ) : null}
                                  {item.is_parent_level ? (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      label={`Parent: ${item.parent_company_name || 'Unassigned'}`}
                                    />
                                  ) : null}
                                  {item.is_parent_level && item.suggested_child_client?.client_name ? (
                                    <Tooltip title={item.suggested_child_client.reason || 'AI suggestion'} arrow>
                                      <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        label={`Suggested: ${item.suggested_child_client.client_name}`}
                                      />
                                    </Tooltip>
                                  ) : null}
                                </Stack>
                              </Stack>
                            }
                            secondary={
                              <Stack spacing={0.5} mt={0.75}>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {item.client_name || item.parent_company_name || 'Unassigned'}{budgetTier ? ` ${budgetTier}` : ''} &middot; From {item.from_email}
                                </Typography>
                                {addresses.length > 0 ? (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    To {addresses.join(', ')}
                                  </Typography>
                                ) : null}
                                <Typography variant="body2" color="text.secondary" noWrap>
                                  {item.summary || item.body_preview || 'No summary available'}
                                </Typography>
                                {item.assigned_owner_name || item.assigned_owner_email ? (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    Owner: {item.assigned_owner_name || item.assigned_owner_email}
                                  </Typography>
                                ) : null}
                                <Typography variant="caption" sx={{ color: timeColor }}>
                                  {timeAgo || 'Unknown'}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </CardContent>
              {totalPages > 1 ? (
                <Box px={2} py={2} display="flex" justifyContent="center">
                  <Pagination
                    color="primary"
                    count={totalPages}
                    page={Math.min(page, totalPages)}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              ) : null}
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            lg={!isMobile && !showAiBrain ? 9 : 5}
            sx={{ display: isMobile && mobilePanel !== 'detail' ? 'none' : 'block' }}
          >
            <Card sx={{ minHeight: isMobile ? 'auto' : 480, borderRadius: 3 }}>
              <CardContent sx={{ px: isMobile ? 1.5 : 3 }}>
                {!isMobile && selectedMessage ? (
                  <Stack direction="row" justifyContent="flex-end" mb={1.5}>
                    <Button
                      size="small"
                      variant={showAiBrain ? 'text' : 'outlined'}
                      startIcon={<PsychologyIcon />}
                      onClick={() => setShowAiBrain((prev) => !prev)}
                    >
                      {showAiBrain ? 'Hide AI Brain' : 'Show AI Brain'}
                    </Button>
                  </Stack>
                ) : null}
                {!selectedMessage ? (
                  <Box py={10} textAlign="center">
                    <Typography variant="body1" color="text.secondary">
                      Select an email from the queue to review the thread, AI decision, and draft.
                    </Typography>
                  </Box>
                ) : detailLoading && !selectedMessageDetail ? (
                  <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress size={32} />
                  </Box>
                ) : (
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        background:
                          'linear-gradient(135deg, rgba(15,23,42,0.04) 0%, rgba(59,130,246,0.10) 100%)',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Stack spacing={1}>
                        <Typography variant="overline" color="text.secondary">
                          Work Area
                        </Typography>
                        <Typography variant="h5">{selectedMessage.subject || 'Untitled email'}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          From {selectedMessage.from_email}
                        </Typography>
                        {normalizeAddresses(selectedMessage.to_addresses).length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            To: {normalizeAddresses(selectedMessage.to_addresses).join(', ')}
                          </Typography>
                        ) : null}
                        {normalizeAddresses(selectedMessage.cc_addresses).length > 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            CC: {normalizeAddresses(selectedMessage.cc_addresses).join(', ')}
                          </Typography>
                        ) : null}
                        <Typography variant="body2" color="text.secondary">
                          Received{' '}
                          {selectedMessage.received_at
                            ? dayjs(selectedMessage.received_at).format('MMM D, YYYY h:mm A')
                            : 'Unknown'}
                        </Typography>
                        {selectedMessage.is_follow_up && (
                          <Alert severity="warning" sx={{ py: 0, mb: 0.5 }}>
                            Follow-up reminder triggered — client has not replied.
                            {selectedMessage.follow_up_note ? ` Note: ${selectedMessage.follow_up_note}` : ''}
                          </Alert>
                        )}
                        {!selectedMessage.is_follow_up && selectedMessage.follow_up_at && (
                          <Alert severity="info" sx={{ py: 0, mb: 0.5 }}>
                            Follow-up set for {dayjs(selectedMessage.follow_up_at).format('ddd MMM D, h:mm A')}
                          </Alert>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {selectedMessage.processing_status ? (
                            <Chip
                              label={
                                STATUS_LABEL_MAP[selectedMessage.processing_status] ||
                                selectedMessage.processing_status
                              }
                              color={
                                STATUS_COLOR_MAP[selectedMessage.processing_status] || 'default'
                              }
                              size="small"
                            />
                          ) : null}
                          {typeof selectedMessage.llm_confidence === 'number' ? (
                            <Chip
                              label={`Confidence ${(selectedMessage.llm_confidence * 100).toFixed(0)}%`}
                              size="small"
                              color="secondary"
                            />
                          ) : null}
                          {selectedMessage.intent ? (
                            <Chip label={`Intent: ${selectedMessage.intent}`} size="small" />
                          ) : null}
                          {workDecisionMeta ? (
                            <Chip
                              label={workDecisionMeta.label}
                              size="small"
                              color={workDecisionMeta.color}
                            />
                          ) : null}
                          {selectedMessage.created_task_title ? (
                            <Chip
                              label={`Task: ${selectedMessage.created_task_title}`}
                              size="small"
                              color="primary"
                            />
                          ) : null}
                          {selectedMessage.assigned_owner_name || selectedMessage.assigned_owner_email ? (
                            <Chip
                              label={`Owner: ${
                                selectedMessage.assigned_owner_name || selectedMessage.assigned_owner_email
                              }`}
                              size="small"
                              variant="outlined"
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </Box>

                    {selectedMessage.is_parent_level ? (
                      <ParentLevelAssignBanner
                        thread={selectedMessage}
                        clients={clients}
                        onAssigned={(updated) => {
                          setSelectedMessageDetail(updated);
                          setMessages((prev) =>
                            prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
                          );
                          loadDashboardSummary();
                        }}
                        onError={(err) => {
                          const detail = err?.response?.data?.detail
                            || err?.response?.data?.client_id?.[0]
                            || 'Unable to assign client.';
                          message.error(detail);
                        }}
                      />
                    ) : null}

                    {/* Quick Actions */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {selectedMessage.conversation_status !== 'closed' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          onClick={() => {
                            clientInboxApi.updateConversationStatus(selectedMessage.id, { conversation_status: 'closed' })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'closed' } : m));
                                loadDashboardSummary();
                              });
                          }}
                        >
                          Close
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            clientInboxApi.updateConversationStatus(selectedMessage.id, { conversation_status: 'needs_response' })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'needs_response' } : m));
                                loadDashboardSummary();
                              });
                          }}
                        >
                          Reopen
                        </Button>
                      )}
                      {selectedMessage.conversation_status !== 'in_progress' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={() => {
                            clientInboxApi.updateConversationStatus(selectedMessage.id, { conversation_status: 'in_progress' })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'in_progress' } : m));
                                loadDashboardSummary();
                              });
                          }}
                        >
                          In Progress
                        </Button>
                      ) : null}
                      <FormControl size="small" sx={{ minWidth: 130 }}>
                        <Select
                          displayEmpty
                          value=""
                          renderValue={() => 'Snooze'}
                          size="small"
                          sx={{ fontSize: 13, height: 31 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            let snoozeUntil;
                            if (val === '1h') snoozeUntil = dayjs().add(1, 'hour');
                            else if (val === '4h') snoozeUntil = dayjs().add(4, 'hour');
                            else if (val === 'tomorrow') snoozeUntil = dayjs().add(1, 'day').hour(9).minute(0).second(0);
                            else if (val === 'friday') {
                              const fri = dayjs().day(5);
                              snoozeUntil = (fri.isBefore(dayjs()) ? fri.add(1, 'week') : fri).hour(9).minute(0).second(0);
                            } else if (val === 'monday') {
                              const mon = dayjs().day(1);
                              snoozeUntil = (mon.isBefore(dayjs()) || mon.isSame(dayjs(), 'day') ? mon.add(1, 'week') : mon).hour(9).minute(0).second(0);
                            }
                            if (!snoozeUntil) return;
                            clientInboxApi.updateConversationStatus(selectedMessage.id, {
                              conversation_status: 'snoozed',
                              snoozed_until: snoozeUntil.toISOString(),
                            })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'snoozed' } : m));
                                loadDashboardSummary();
                                message.success(`Snoozed until ${snoozeUntil.format('ddd h:mm A')}`);
                              });
                          }}
                        >
                          <MenuItem value="1h">1 hour</MenuItem>
                          <MenuItem value="4h">4 hours</MenuItem>
                          <MenuItem value="tomorrow">Tomorrow 9am</MenuItem>
                          <MenuItem value="friday">Friday 9am</MenuItem>
                          <MenuItem value="monday">Monday 9am</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                          displayEmpty
                          value=""
                          renderValue={() => (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <AccessAlarmIcon sx={{ fontSize: 14 }} />
                              <span>Follow-up</span>
                            </Stack>
                          )}
                          size="small"
                          sx={{ fontSize: 13, height: 31 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (!val) return;
                            let followUpAt;
                            if (val === '3d') followUpAt = dayjs().add(3, 'day').hour(9).minute(0).second(0);
                            else if (val === '5d') followUpAt = dayjs().add(5, 'day').hour(9).minute(0).second(0);
                            else if (val === '1w') followUpAt = dayjs().add(1, 'week').hour(9).minute(0).second(0);
                            else if (val === '2w') followUpAt = dayjs().add(2, 'week').hour(9).minute(0).second(0);
                            if (!followUpAt) return;
                            clientInboxApi.updateConversationStatus(selectedMessage.id, {
                              conversation_status: 'awaiting_client',
                              follow_up_at: followUpAt.toISOString(),
                              follow_up_note: '',
                            })
                              .then((res) => {
                                setSelectedMessageDetail(res.data);
                                setMessages((prev) => prev.map((m) => m.id === res.data.id ? { ...m, conversation_status: 'awaiting_client', follow_up_at: res.data.follow_up_at } : m));
                                loadDashboardSummary();
                                message.success(`Follow-up set for ${followUpAt.format('ddd MMM D')}`);
                              });
                          }}
                        >
                          <MenuItem value="3d">3 days</MenuItem>
                          <MenuItem value="5d">5 days</MenuItem>
                          <MenuItem value="1w">1 week</MenuItem>
                          <MenuItem value="2w">2 weeks</MenuItem>
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setForwardOpen(true)}
                      >
                        Forward
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          clientInboxApi.claimThread(selectedMessage.id)
                            .then((res) => {
                              const updated = res.data;
                              setSelectedMessageDetail(updated);
                              setMessages((prev) => prev.map((m) => m.id === updated.id ? { ...m, assigned_owner_name: updated.assigned_owner_name, assigned_owner_email: updated.assigned_owner_email } : m));
                              message.success('Claimed.');
                            });
                        }}
                      >
                        Claim
                      </Button>
                      {selectedMessage.conversation_status ? (
                        <Chip
                          label={CONVERSATION_STATUS_LABEL_MAP[selectedMessage.conversation_status] || selectedMessage.conversation_status}
                          color={CONVERSATION_STATUS_COLOR_MAP[selectedMessage.conversation_status] || 'default'}
                          size="small"
                        />
                      ) : null}
                      {selectedMessage.assigned_owner_name ? (
                        <Chip
                          label={selectedMessage.assigned_owner_name}
                          size="small"
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>

                    {workDecisionMeta ? (
                      <Alert severity={workDecisionMeta.severity}>
                        {workDecisionMeta.message}
                        {selectedQuestionRouter?.reason ? ` ${selectedQuestionRouter.reason}` : ''}
                      </Alert>
                    ) : null}

                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderColor:
                          workDecisionMeta?.severity === 'error'
                            ? 'error.light'
                            : workDecisionMeta?.severity === 'warning'
                            ? 'warning.light'
                            : workDecisionMeta?.severity === 'success'
                            ? 'success.light'
                            : 'divider',
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                            <Typography variant="subtitle1">AI Triage</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {workDecisionMeta ? (
                                <Chip
                                  label={workDecisionMeta.label}
                                  color={workDecisionMeta.color}
                                  size="small"
                                />
                              ) : null}
                              {selectedQuestionRouter?.category ? (
                                <Chip
                                  label={selectedQuestionRouter.category.replace(/_/g, ' ')}
                                  variant="outlined"
                                  size="small"
                                />
                              ) : null}
                            </Stack>
                          </Stack>
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Recommended handling
                              </Typography>
                              <Typography variant="body2">
                                {workDecisionMeta?.label || 'No routing decision yet'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Router reason
                              </Typography>
                              <Typography variant="body2">
                                {selectedQuestionRouter?.reason || 'No router reason available'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Reply target
                              </Typography>
                              <Typography variant="body2">
                                {replyToValue || 'No reply recipient selected'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary">
                                Owner
                              </Typography>
                              <Typography variant="body2">
                                {selectedMessage.assigned_owner_name ||
                                  selectedMessage.assigned_owner_email ||
                                  'No owner assigned yet'}
                              </Typography>
                              {selectedOwnerAssignment?.reason ? (
                                <Typography variant="caption" color="text.secondary">
                                  {selectedOwnerAssignment.reason}
                                </Typography>
                              ) : null}
                            </Grid>
                          </Grid>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1">Reply Composer</Typography>
                          {selectedMessage.is_parent_level ? (
                            <Alert severity="info">
                              Assign this thread to a specific client before drafting a reply.
                            </Alert>
                          ) : null}
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              variant="outlined"
                              startIcon={<SaveIcon />}
                              onClick={handleSaveDraft}
                              disabled={!draftChanged || savingDraft || selectedMessage.is_parent_level}
                            >
                              {savingDraft ? 'Saving…' : 'Save draft'}
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={<SendIcon />}
                              onClick={handleSendReply}
                              disabled={
                                sendingReply || !draftValue.trim() || !replyFromValue || sendBlocked || selectedMessage.is_parent_level
                              }
                            >
                              {sendingReply ? 'Sending…' : 'Send reply'}
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<ScheduleSendIcon />}
                              onClick={(e) => setScheduleAnchor(e.currentTarget)}
                              disabled={!draftValue.trim() || !replyFromValue || sendBlocked}
                            >
                              Schedule
                            </Button>
                            <Popover
                              open={Boolean(scheduleAnchor)}
                              anchorEl={scheduleAnchor}
                              onClose={() => setScheduleAnchor(null)}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                              <Stack sx={{ p: 1.5, minWidth: 180 }} spacing={0.5}>
                                <Typography variant="caption" fontWeight={600}>Schedule send</Typography>
                                {[
                                  { label: 'Tomorrow 9am', value: dayjs().add(1, 'day').hour(9).minute(0).second(0) },
                                  { label: 'Friday 9am', value: (() => { const fri = dayjs().day(5); return (fri.isBefore(dayjs()) ? fri.add(1, 'week') : fri).hour(9).minute(0).second(0); })() },
                                  { label: 'Monday 9am', value: (() => { const mon = dayjs().day(1); return (mon.isBefore(dayjs()) || mon.isSame(dayjs(), 'day') ? mon.add(1, 'week') : mon).hour(9).minute(0).second(0); })() },
                                  { label: 'In 2 hours', value: dayjs().add(2, 'hour') },
                                  { label: 'In 4 hours', value: dayjs().add(4, 'hour') },
                                ].map((opt) => (
                                  <MenuItem
                                    key={opt.label}
                                    dense
                                    onClick={() => handleScheduleReply(opt.value.toISOString())}
                                  >
                                    {opt.label} — {opt.value.format('ddd h:mm A')}
                                  </MenuItem>
                                ))}
                              </Stack>
                            </Popover>
                          </Stack>
                          {scheduledReplies.length > 0 && (
                            <Stack spacing={0.5} mt={0.5}>
                              {scheduledReplies.map((sr) => (
                                <Alert
                                  key={sr.id}
                                  severity="info"
                                  sx={{ py: 0, '& .MuiAlert-message': { width: '100%' } }}
                                  action={
                                    <Button size="small" color="inherit" onClick={() => handleCancelScheduled(sr.id)}>
                                      Cancel
                                    </Button>
                                  }
                                >
                                  <Typography variant="caption">
                                    Scheduled for {dayjs(sr.scheduled_at).format('ddd MMM D, h:mm A')} from {sr.from_email}
                                  </Typography>
                                </Alert>
                              ))}
                            </Stack>
                          )}
                          <Stack spacing={1}>
                            <TextField
                              select
                              label="From"
                              value={replyFromValue}
                              onChange={(event) => setReplyFromValue(event.target.value)}
                              helperText={
                                senderOptions.length === 0
                                  ? 'No sender addresses configured'
                                  : 'Choose which inbox to send from'
                              }
                              fullWidth
                              disabled={senderOptions.length === 0}
                            >
                              {senderOptions.map((sender) => (
                                <MenuItem key={sender} value={sender}>
                                  {sender}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label="To"
                              value={replyToValue}
                              onChange={(event) => setReplyToValue(event.target.value)}
                              helperText="Defaults to the latest external sender"
                              fullWidth
                            />
                            <TextField
                              label="CC"
                              value={replyCcValue}
                              onChange={(event) => setReplyCcValue(event.target.value)}
                              helperText="Optional"
                              fullWidth
                            />
                            <TextField
                              label="Subject"
                              value={replySubject}
                              onChange={(event) => setReplySubject(event.target.value)}
                              fullWidth
                            />
                          </Stack>
                          {isLockedBudgetConfirmationDraft ? (
                            <Stack spacing={1} mt={1}>
                              <Alert severity="info">
                                Budget template is shown as sent to preserve exact HTML formatting.
                              </Alert>
                              <Box
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  maxHeight: 380,
                                  overflow: 'auto',
                                }}
                                dangerouslySetInnerHTML={{ __html: draftValue }}
                              />
                              <TextField
                                multiline
                                minRows={3}
                                fullWidth
                                label="Add text under budget table (optional)"
                                value={budgetNote}
                                onChange={(event) => setBudgetNote(event.target.value)}
                                placeholder="Type your additional line(s)..."
                              />
                            </Stack>
                          ) : (
                            <TextEditor
                              ref={draftEditorRef}
                              content={draftValue}
                              onUpdate={({ editor }) => setDraftValue(editor.getHTML())}
                              placeholder="Draft reply"
                            />
                          )}
                          {isListening && voiceTarget === 'draft' && (
                            <Alert severity="info" sx={{ py: 0.25, mt: 0.5 }}>
                              Listening… you can keep typing while the mic is on. Click the mic button to stop.
                              {interimVoiceText && (
                                <Box component="span" sx={{ display: 'block', fontStyle: 'italic', opacity: 0.8, mt: 0.25 }}>
                                  {interimVoiceText}
                                </Box>
                              )}
                            </Alert>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mt={1}>
                            <Button
                              size="small"
                              variant="text"
                              component="label"
                            >
                              Attach files
                              <input
                                type="file"
                                multiple
                                hidden
                                onChange={(e) => {
                                  setReplyAttachments((prev) => [...prev, ...Array.from(e.target.files)]);
                                  e.target.value = '';
                                }}
                              />
                            </Button>
                            <Tooltip title={isListening && voiceTarget === 'draft' ? 'Stop listening' : 'Dictate reply with voice'}>
                              <Button
                                size="small"
                                variant={isListening && voiceTarget === 'draft' ? 'contained' : 'text'}
                                color={isListening && voiceTarget === 'draft' ? 'error' : 'inherit'}
                                onClick={() => toggleVoiceInput('draft')}
                                disabled={isListening && voiceTarget !== 'draft'}
                                startIcon={isListening && voiceTarget === 'draft' ? <MicOffIcon /> : <MicIcon />}
                              >
                                {isListening && voiceTarget === 'draft' ? 'Stop' : 'Voice'}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Polish rough text into a professional email using AI">
                              <span>
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={handlePolishDraft}
                                  disabled={polishLoading || !draftValue.trim()}
                                  startIcon={<AutoAwesomeIcon />}
                                >
                                  {polishLoading ? 'Polishing…' : 'Polish with AI'}
                                </Button>
                              </span>
                            </Tooltip>
                            {replyAttachments.map((file, idx) => (
                              <Chip
                                key={`${file.name}-${idx}`}
                                label={file.name}
                                size="small"
                                onDelete={() => setReplyAttachments((prev) => prev.filter((_, i) => i !== idx))}
                              />
                            ))}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={0}>
                          <Typography variant="subtitle1" mb={1.5}>
                            Conversation ({selectedMessage.messages?.length || 0} messages)
                          </Typography>
                          {(() => {
                            const sorted = (selectedMessage.messages || [])
                              .slice()
                              .sort((a, b) => {
                                const ta = new Date(a.received_at || a.created_at).getTime();
                                const tb = new Date(b.received_at || b.created_at).getTime();
                                if (ta !== tb) return tb - ta;
                                return (b.id || 0) - (a.id || 0);
                              });
                            return sorted.map((msg, idx) => (
                              <ThreadMessage
                                key={msg.id}
                                msg={msg}
                                defaultExpanded={idx === 0}
                                isInternal={msg.is_internal}
                              />
                            ));
                          })()}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            lg={4}
            sx={{
              display:
                (isMobile && mobilePanel !== 'detail') || (!isMobile && !showAiBrain)
                  ? 'none'
                  : 'block',
            }}
          >
            {isMobile ? (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => setShowMobileSidebar((prev) => !prev)}
                endIcon={<ExpandMoreIcon sx={{ transform: showMobileSidebar ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
                sx={{ mb: 2 }}
              >
                {showMobileSidebar ? 'Hide AI Brain & Actions' : 'Show AI Brain & Actions'}
              </Button>
            ) : (
              <Stack direction="row" justifyContent="flex-end" mb={1}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ChevronRightIcon />}
                  onClick={() => setShowAiBrain(false)}
                >
                  Hide AI Brain
                </Button>
              </Stack>
            )}
            <Collapse in={!isMobile || showMobileSidebar} timeout="auto">
            <Stack spacing={3}>
              <Card sx={{ borderRadius: 3, position: { lg: 'sticky' }, top: { lg: 24 } }}>
                <CardContent>
                  {selectedMessage ? (
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="overline" color="text.secondary">
                          AI Brain
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleInspectContext()}
                            disabled={inspectLoading}
                          >
                            {inspectLoading ? 'Loading…' : 'Load context'}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleRegenerateDraft}
                            disabled={regenerateLoading}
                          >
                            {regenerateLoading ? 'Regenerating…' : 'Regenerate draft'}
                          </Button>
                        </Stack>
                      </Stack>
                      <TextField
                        size="small"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={3}
                        placeholder="Add one-time context for AI draft (e.g. 'mention the promotion ending Friday', 'keep it brief')..."
                        value={draftContextNote}
                        onChange={(e) => setDraftContextNote(e.target.value)}
                        InputProps={{
                          sx: { fontSize: '0.8rem' },
                          endAdornment: (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
                              <Tooltip title={isListening && voiceTarget === 'context' ? 'Stop listening' : 'Dictate context with voice'}>
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleVoiceInput('context')}
                                    disabled={isListening && voiceTarget !== 'context'}
                                    sx={{ color: isListening && voiceTarget === 'context' ? 'error.main' : 'action.active' }}
                                  >
                                    {isListening && voiceTarget === 'context' ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        helperText={
                          isListening && voiceTarget === 'context'
                            ? `Listening… you can keep typing. Click the mic to stop.${interimVoiceText ? ` (${interimVoiceText})` : ''}`
                            : draftContextNote
                              ? 'This context will be included in the next regeneration only'
                              : ''
                        }
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleInspectContext({ includeBudgetPacing: true })}
                          disabled={inspectLoading}
                        >
                          Pacing
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleInspectContext({ includePrompt: true })}
                          disabled={inspectLoading}
                        >
                          Prompt
                        </Button>
                      </Stack>
                      {inspectError ? <Alert severity="error">{inspectError}</Alert> : null}
                      <Tabs
                        value={inspectTab}
                        onChange={(_, value) => setInspectTab(value)}
                        variant="scrollable"
                        allowScrollButtonsMobile
                      >
                        <Tab value="sources" label="Sources" />
                        <Tab value="changes" label="Changes" />
                        <Tab value="routing" label="Routing" />
                        <Tab value="profile" label="Profile" />
                        <Tab value="knowledge" label="Knowledge" />
                        <Tab value="memory" label="Memory" />
                        <Tab value="live" label="Live Data" />
                        <Tab value="recent" label="Recent" />
                        <Tab value="prompt" label="Prompt" />
                      </Tabs>
                      {renderInspectorSection()}

                      <Divider sx={{ my: 1 }} />

                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="overline" color="text.secondary">
                            <BookmarkAddIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                            Quick Capture
                          </Typography>
                        </Stack>
                        <ToggleButtonGroup
                          value={captureScope}
                          exclusive
                          onChange={(_, val) => { if (val) setCaptureScope(val); }}
                          size="small"
                          fullWidth
                          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', py: 0.4 } }}
                        >
                          <ToggleButton value="auto">
                            Auto (AI decides)
                          </ToggleButton>
                          <ToggleButton value="client">
                            {selectedMessage?.client_name || 'Client'}
                          </ToggleButton>
                          <ToggleButton value="company">
                            Company-wide
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          minRows={2}
                          maxRows={5}
                          placeholder="e.g. 'This client prefers email over phone. Their busy season is June through August.' or 'Our agency policy is to never pause campaigns without manager approval.'"
                          value={captureText}
                          onChange={(e) => setCaptureText(e.target.value)}
                          InputProps={{
                            sx: { fontSize: '0.8rem' },
                            endAdornment: (
                              <InputAdornment position="end" sx={{ alignSelf: 'flex-end', mb: 0.5 }}>
                                <Tooltip title={isListening && voiceTarget === 'capture' ? 'Stop listening' : 'Dictate with voice'}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => toggleVoiceInput('capture')}
                                      disabled={isListening && voiceTarget !== 'capture'}
                                      sx={{ color: isListening && voiceTarget === 'capture' ? 'error.main' : 'action.active' }}
                                    >
                                      {isListening && voiceTarget === 'capture' ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          helperText={
                            isListening && voiceTarget === 'capture'
                              ? 'Listening… click the mic to stop'
                              : ''
                          }
                        />
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleQuickCapture}
                          disabled={captureLoading || !captureText.trim()}
                          startIcon={<BookmarkAddIcon />}
                        >
                          {captureLoading ? 'Saving…' : 'Capture to Knowledge Base'}
                        </Button>
                        {captureResult && (captureResult.created?.length > 0 || captureResult.updated?.length > 0 || captureResult.skipped?.length > 0) && (
                          <Stack spacing={0.5}>
                            {(captureResult.created || []).map((rec, idx) => (
                              <Alert key={`c-${idx}`} severity="success" sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                                <strong>{rec.record_type.replace('_', ' ')}</strong>:{' '}
                                <RouterLink
                                  to={`/knowledge-base?type=${rec.record_type}&id=${rec.id}`}
                                  target="_blank"
                                  style={{ color: 'inherit' }}
                                >
                                  {rec.title} <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
                                </RouterLink>
                                {rec.tags && rec.tags.length > 0 && (
                                  <> — {rec.tags.map((tag) => `#${tag}`).join(' ')}</>
                                )}
                              </Alert>
                            ))}
                            {(captureResult.updated || []).map((rec, idx) => (
                              <Alert key={`u-${idx}`} severity="info" sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                                <strong>Updated {rec.record_type.replace('_', ' ')}</strong>:{' '}
                                <RouterLink
                                  to={`/knowledge-base?type=${rec.record_type}&id=${rec.id}`}
                                  target="_blank"
                                  style={{ color: 'inherit' }}
                                >
                                  {rec.title} <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
                                </RouterLink>
                                {rec.conflict_note && <> — {rec.conflict_note}</>}
                              </Alert>
                            ))}
                            {(captureResult.skipped || []).map((rec, idx) => (
                              <Alert key={`s-${idx}`} severity="warning" sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                                <strong>Skipped {rec.record_type.replace('_', ' ')}</strong>: {rec.title}
                                {rec.skip_reason && <> — {rec.skip_reason}</>}
                              </Alert>
                            ))}
                          </Stack>
                        )}
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select an email to load routing, knowledge, memory, and live context.
                    </Typography>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1">Suggested Actions</Typography>
                    {selectedMessage &&
                    Array.isArray(selectedMessage.suggested_actions) &&
                    selectedMessage.suggested_actions.length > 0 ? (
                      <Stack spacing={1}>
                        {selectedMessage.suggested_actions.map((action, index) =>
                          renderSuggestedAction(action, index),
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No suggested actions provided by the analysis.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              {selectedMessage?.relevant_links?.length > 0 ? (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Quick Links
                      </Typography>
                      <Stack spacing={0.5}>
                        {selectedMessage.relevant_links.map((link) => (
                          <Button
                            key={link.url}
                            size="small"
                            variant="text"
                            href={link.url}
                            target="_blank"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              fontWeight: 500,
                              fontSize: 13,
                              py: 0.5,
                              px: 1,
                              color: 'primary.main',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            {link.label}
                          </Button>
                        ))}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ) : null}

              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1">Internal Notes</Typography>
                    {selectedMessage?.notes && selectedMessage.notes.length > 0 ? (
                      <Stack spacing={1}>
                        {selectedMessage.notes.map((note) => (
                          <Box key={note.id} p={1.5} borderRadius={1} bgcolor="grey.50">
                            <Typography variant="body2">{note.body}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {note.author_name || note.author_email || 'Unknown'} &middot;{' '}
                              {dayjs(note.created_at).fromNow()}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No internal notes yet.
                      </Typography>
                    )}
                    {selectedMessage ? (
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="Add a note..."
                          id="thread-note-input"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              const body = e.target.value.trim();
                              if (!body) return;
                              clientInboxApi.createThreadNote(selectedMessage.id, { body })
                                .then((res) => {
                                  setSelectedMessageDetail((prev) => ({
                                    ...prev,
                                    notes: [res.data, ...(prev?.notes || [])],
                                  }));
                                  e.target.value = '';
                                });
                            }
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            const input = document.getElementById('thread-note-input');
                            const body = (input?.value || '').trim();
                            if (!body) return;
                            clientInboxApi.createThreadNote(selectedMessage.id, { body })
                              .then((res) => {
                                setSelectedMessageDetail((prev) => ({
                                  ...prev,
                                  notes: [res.data, ...(prev?.notes || [])],
                                }));
                                input.value = '';
                              });
                          }}
                        >
                          Add
                        </Button>
                      </Stack>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1">Task Workflow</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<TaskIcon />}
                        onClick={handleCreateTask}
                        disabled={!selectedMessage || Boolean(selectedMessage?.created_task) || creatingTask}
                      >
                        {selectedMessage?.created_task
                          ? 'Task already linked'
                          : creatingTask
                          ? 'Creating task…'
                          : createTaskRecommended
                          ? 'Create recommended task'
                          : 'Create task from email'}
                      </Button>
                      <Button
                        variant="outlined"
                        component={RouterLink}
                        to="/tasks"
                        startIcon={<TaskIcon />}
                      >
                        View task board
                      </Button>
                    </Stack>
                    {selectedMessage?.processing_notes ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Processing notes
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                        >
                          {selectedMessage.processing_notes}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No processing notes available.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
            </Collapse>
          </Grid>
        </Grid>
      </Stack>

      {/* Compose New Email Dialog */}
      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              size="small"
              options={clientOptions}
              loading={clientsLoading}
              onInputChange={handleClientSearch}
              getOptionLabel={(option) => option.label || ''}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              renderInput={(params) => (
                <TextField {...params} label="Client" placeholder="Search clients..." />
              )}
              onChange={(_, option) => {
                const el = document.getElementById('compose-client-id');
                if (el) el.value = option ? String(option.value) : '';
              }}
            />
            <input type="hidden" id="compose-client-id" />
            <TextField
              select
              label="From"
              defaultValue={defaultSender}
              fullWidth
              size="small"
              id="compose-from"
            >
              {senderOptions.map((sender) => (
                <MenuItem key={sender} value={sender}>{sender}</MenuItem>
              ))}
            </TextField>
            <TextField label="To" fullWidth size="small" id="compose-to" placeholder="recipient@example.com" />
            <TextField label="CC" fullWidth size="small" id="compose-cc" placeholder="Optional" />
            <TextField label="Subject" fullWidth size="small" id="compose-subject" />
            <TextEditor
              content=""
              onUpdate={({ editor }) => {
                const el = document.getElementById('compose-body-value');
                if (el) el.value = editor.getHTML();
              }}
              placeholder="Write your email..."
            />
            <input type="hidden" id="compose-body-value" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={composeSending}
            onClick={() => {
              const clientId = document.getElementById('compose-client-id')?.value;
              const fromEmail = document.getElementById('compose-from')?.value;
              const to = (document.getElementById('compose-to')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const cc = (document.getElementById('compose-cc')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const subject = document.getElementById('compose-subject')?.value || '';
              const body = document.getElementById('compose-body-value')?.value || '';
              if (!clientId || !to.length || !subject || !body) {
                message.error('Client, to, subject, and body are required.');
                return;
              }
              setComposeSending(true);
              clientInboxApi.composeEmail({ client: clientId, from_email: fromEmail, to, cc, subject, body })
                .then((res) => {
                  message.success('Email sent.');
                  setComposeOpen(false);
                  loadMessages();
                  loadDashboardSummary();
                  if (res.data?.id) {
                    setSelectedId(res.data.id);
                    setSearchParams({ thread: String(res.data.id) });
                  }
                })
                .catch((err) => {
                  console.error('Failed to compose email', err);
                  message.error(err?.response?.data?.detail || 'Failed to send email.');
                })
                .finally(() => setComposeSending(false));
            }}
          >
            {composeSending ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Undo Send Banner */}
      {undoSendData ? (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: '#323232',
            color: '#fff',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 1400,
            boxShadow: 6,
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff' }}>Sending reply...</Typography>
          <Button
            size="small"
            sx={{ color: '#ffb74d', fontWeight: 700 }}
            onClick={() => {
              if (undoSendData?.timer) {
                clearTimeout(undoSendData.timer);
              }
              setUndoSendData(null);
              setSendingReply(false);
              message.success('Send cancelled.');
            }}
          >
            Undo
          </Button>
        </Box>
      ) : null}

      {/* Forward Email Dialog */}
      <Dialog open={forwardOpen} onClose={() => setForwardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Forward Email</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="From"
              defaultValue={defaultSender}
              fullWidth
              size="small"
              id="forward-from"
            >
              {senderOptions.map((sender) => (
                <MenuItem key={sender} value={sender}>{sender}</MenuItem>
              ))}
            </TextField>
            <TextField label="To" fullWidth size="small" id="forward-to" placeholder="recipient@example.com" />
            <TextField label="CC" fullWidth size="small" id="forward-cc" placeholder="Optional" />
            <TextField
              label="Add a note (optional)"
              fullWidth
              size="small"
              multiline
              minRows={2}
              id="forward-body"
              placeholder="Add a message above the forwarded content..."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForwardOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={forwardSending}
            onClick={() => {
              const fromEmail = document.getElementById('forward-from')?.value;
              const to = (document.getElementById('forward-to')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const cc = (document.getElementById('forward-cc')?.value || '').split(',').map((s) => s.trim()).filter(Boolean);
              const body = document.getElementById('forward-body')?.value || '';
              if (!to.length) {
                message.error('At least one recipient is required.');
                return;
              }
              setForwardSending(true);
              clientInboxApi.forwardThread(selectedMessage.id, { from_email: fromEmail, to, cc, body })
                .then(() => {
                  message.success('Email forwarded.');
                  setForwardOpen(false);
                })
                .catch((err) => {
                  console.error('Failed to forward', err);
                  message.error(err?.response?.data?.detail || 'Failed to forward email.');
                })
                .finally(() => setForwardSending(false));
            }}
          >
            {forwardSending ? 'Forwarding...' : 'Forward'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ClientInboxPageWithBoundary() {
  return (
    <InboxErrorBoundary>
      <ClientInboxPage />
    </InboxErrorBoundary>
  );
}

export default ClientInboxPageWithBoundary;
